import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BookOpen, Play, Download, RefreshCw, ArrowLeft, Bold, Italic, Heading2, Image as ImageIcon, Link2, CornerDownLeft, Sparkles, Map, Heart, Copy, Network, ChevronDown, ChevronUp, FileDown, Save, Gamepad2, Code, Table2, Loader2, Info } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PageHeader from './PageHeader';
import { generateMindMapHtml } from '../utils/mindMapCompiler';
import { generateStoryHtml, parseTwee, splitLongPassages } from '../utils/storyGenerator';
import { generateWordPressStoryHtml } from '../utils/exportWordPressStory';
import AIStoryGeneratorPanel from './AIStoryGeneratorPanel';
import AIUsageLimitTracker from './AIUsageLimitTracker';
import LiveSessionPanel from './LiveSessionPanel';
import VersionBadge from './VersionBadge';
import SEO from './SEO';
import JSZip from 'jszip';
import { getStoryOptions, getStoryById } from '../data/storyLibrary';
import { API_BASE_URL } from '../config/api';
import PathNavStyleConfigurator from './PathNavStyleConfigurator';

// Compiler-specific utilities (extracted per RULES.md)
import { processContent, convertGraphToRuntimeStory, convertRuntimeToTweeFormat, filterTweeByNodeLevel } from '../utils/compiler';
// Compiler sub-components (extracted per RULES.md)
import { CompilerActionBar, ExampleBanner, StepSection, ColorPickerField } from './compiler';
import CompileErrorDialog from './CompileErrorDialog';

const SAMPLE_TWEE = `:: StoryTitle
My Interactive Story

:: Start
You wake up in a **mysterious forest**. The morning mist swirls around ancient trees, and you hear distant sounds echoing through the woods.

The trees are *ancient and gnarled*, their bark covered in strange moss.

!What do you do?

[[Explore deeper into the forest|Forest]]
[[Look for a path out|Path]]
[[Call out for help|CallOut]]

:: Forest
You venture deeper into the forest. The trees grow denser, and shafts of sunlight pierce through the canopy above. You notice ~~faint~~ strange markings on some of the tree trunks.

As you walk, you come across a small clearing with a peculiar stone altar in the center.

[[Examine the altar|Altar]]
[[Continue walking|DeepForest]]
[[Return to start|Start]]

:: Path
You search for a way out and discover a narrow dirt path leading through the undergrowth. Following it, you emerge onto a wider trail that seems to lead toward civilization.

After walking for several minutes, you see **smoke rising** in the distance.

[[Head toward the smoke|Village]]
[[Stay on the main path|MainPath]]
[[Go back|Start]]

:: CallOut
You cup your hands around your mouth and call out loudly. Your voice echoes through the trees, but there's no immediate response.

After a moment of silence, you hear rustling in the bushes nearby.

[[Investigate the sound|Investigate]]
[[Run away|Start]]
[[Call out again|CallOut2]]

:: Altar
The stone altar is covered in moss and ancient carvings. In the center, there's a small depression that might have once held something valuable.

You notice the carvings seem to tell a story of an *ancient civilization* that once lived in this forest.

[[Take a closer look at the carvings|Carvings]]
[[Leave the altar alone|Forest]]

:: DeepForest
You continue deeper into the forest. The air grows cooler and the sounds of civilization fade completely. You're truly alone now.

This might not have been the best choice.

[[Turn back|Forest]]

:: Village
Following the smoke, you discover a small village nestled in the forest. Friendly locals greet you and offer you food and directions back to the main road.

**Congratulations!** You've found your way to safety.

[[Start over|Start]]

:: MainPath
You follow the main path for what feels like hours. Eventually, you reach a highway and flag down a passing car.

You've made it out!

[[Start over|Start]]

:: Investigate
Approaching cautiously, you push aside the bushes to find a small, frightened deer. It looks at you with wide eyes before bounding away into the forest.

[[Continue exploring|Forest]]
[[Head back|Start]]

:: CallOut2
You call out again, louder this time. This time, you hear a response! A park ranger emerges from the trees, looking relieved to have found you.

The ranger guides you safely out of the forest.

[[Start over|Start]]

:: Carvings
Studying the carvings more closely, you begin to understand the story they tell. The ancient people who lived here believed the forest was *sacred* and protected by spirits.

You feel a deep connection to this place and its history.

[[Return to exploring|Forest]]
[[Head back to start|Start]]`;

const Compiler = ({ 
  initialTwee = SAMPLE_TWEE, 
  pageTitle = 'TweePorter - Story Compiler', 
  isExample = false,
  exampleCategory = '',
  exampleDescription = '',
  initialLayout = 'bottom'
}) => {
  const navigate = useNavigate();
  const previewRef = useRef(null);
  
  // Check for sitemap import on initial load
  const getInitialTwee = () => {
    if (typeof window !== 'undefined' && !isExample) {
      const sitemapTwee = localStorage.getItem('compiler_twee_code');
      const sitemapAction = localStorage.getItem('compiler_action');
      if (sitemapTwee && sitemapAction === 'import') {
        // Clear any cached compiled content to ensure fresh start
        localStorage.removeItem('compiler_compiled_html');
        return sitemapTwee;
      }
    }
    return initialTwee;
  };
  
  const [tweeCode, setTweeCode] = useState(getInitialTwee);
  const [compiledHtml, setCompiledHtml] = useState('');
  const [mindMapHtml, setMindMapHtml] = useState('');
  const [storyTitle, setStoryTitle] = useState('Interactive Story');
  const [layoutOption, setLayoutOption] = useState(initialLayout); // 'bottom', 'side', 'leftRight'
  const [linkDisplay, setLinkDisplay] = useState('buttons'); // 'buttons' or 'inline'
  const [isCompiling, setIsCompiling] = useState(false);
  // Validator dialog state
  const [validationReport, setValidationReport] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  // Replacement state for window.confirm() — sandboxed iframe blocks native modals
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);
  const [showLoadStoryConfirmDialog, setShowLoadStoryConfirmDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [passageCount, setPassageCount] = useState(0);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePosition, setImagePosition] = useState('center');
  const [showPassageLengthInfo, setShowPassageLengthInfo] = useState(false);
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);
  
  // Link Insert dialog state (replaces window.prompt-based flow that is blocked
  // inside sandboxed iframes such as the App Preview).
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkTarget, setLinkTarget] = useState('');
  const [linkSelStart, setLinkSelStart] = useState(0);
  const [linkSelEnd, setLinkSelEnd] = useState(0);
  
  // Visual Editor config state
  const [visualEditorConfig, setVisualEditorConfig] = useState(null);
  
  // NOTE: filterTweeByNodeLevel is now imported from '../utils/compiler/validateTwee'
  // Import added at top of file. Using imported version from utils.
  
  // Table dialog state
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHeaders, setTableHeaders] = useState(['Header 1', 'Header 2', 'Header 3']);
  const [tableData, setTableData] = useState([
    ['', '', ''],
    ['', '', '']
  ]);
  
  // Style color states
  const [boldColor, setBoldColor] = useState('#fbd38d');
  const [italicColor, setItalicColor] = useState('#b794f4');
  const [headingColor, setHeadingColor] = useState('#90cdf4');
  const [linkColor, setLinkColor] = useState('#667eea');
  const [textBgColor, setTextBgColor] = useState('#ffffff');
  const [frameBgColor, setFrameBgColor] = useState('#f8f9fa');
  
  // Passage length limiting
  const [maxPassageLength, setMaxPassageLength] = useState(0); // 0 = unlimited
  
  // Intro/Maximize View Mode
  const [introMode, setIntroMode] = useState(false);

  // Navigation controls option: 'full', 'minimal', or 'none'
  const [navigationOption, setNavigationOption] = useState('full');
  
  // Track which color field is focused for preset palette
  const [focusedColorField, setFocusedColorField] = useState(null);
  
  // Collapsible sections state - all start minimized
  const [step1Expanded, setStep1Expanded] = useState(false);
  const [step2Expanded, setStep2Expanded] = useState(false);
  const [step2AIExpanded, setStep2AIExpanded] = useState(false);
  const [step2StoryFormatExpanded, setStep2StoryFormatExpanded] = useState(false);
  const [step2StyleFormatExpanded, setStep2StyleFormatExpanded] = useState(false);
  const [step2MindMapExpanded, setStep2MindMapExpanded] = useState(false);
  const [step3Expanded, setStep3Expanded] = useState(false);
  const [step4Expanded, setStep4Expanded] = useState(false);
  
  // Mind Map Layout Options (stored in twee as comments)
  const [mindMapDirection, setMindMapDirection] = useState('right');
  const [mindMapAlignment, setMindMapAlignment] = useState('center');
  const [mindMapShowDesc, setMindMapShowDesc] = useState(true);
  const [mindMapCharLimit, setMindMapCharLimit] = useState(80);
  const [mindMapConfigUserChanged, setMindMapConfigUserChanged] = useState(false); // Track if user explicitly changed config
  
  // Live Session state
  const [showLiveSession, setShowLiveSession] = useState(false);
  const [currentPassage, setCurrentPassage] = useState({ id: 'Start', title: 'Start', content: '' });
  
  // JavaScript Enhanced mode state
  const [hasAdvancedFeatures, setHasAdvancedFeatures] = useState(false);
  const [jsEnhancedHtml, setJsEnhancedHtml] = useState('');
  const [isCompilingJs, setIsCompilingJs] = useState(false);
  
  // Helper to extract passages from Twee code
  const extractPassagesFromTwee = (tweeCode) => {
    const passages = [];
    const passageRegex = /:: ([^\n\[]+)(?:\s*\[([^\]]*)\])?\s*\n([\s\S]*?)(?=\n:: |\n*$)/g;
    let match;
    
    while ((match = passageRegex.exec(tweeCode)) !== null) {
      const title = match[1].trim();
      const tags = match[2] ? match[2].trim() : '';
      const content = match[3].trim();
      
      // Skip metadata passages
      if (!['StoryTitle', 'StoryData', 'StoryAuthor'].includes(title)) {
        passages.push({ id: title, title, tags, content });
      }
    }
    return passages;
  };
  
  // Update current passage when twee code changes or is compiled
  useEffect(() => {
    if (tweeCode) {
      const passages = extractPassagesFromTwee(tweeCode);
      if (passages.length > 0) {
        // Find "Start" passage or use first one
        const startPassage = passages.find(p => p.title === 'Start') || passages[0];
        setCurrentPassage(startPassage);
      }
    }
  }, [tweeCode, compiledHtml]);
  
  // Story selector state
  const [selectedStoryId, setSelectedStoryId] = useState('default-forest');
  
  // Tracking story state
  const [isTrackingStory, setIsTrackingStory] = useState(false);
  const [trackingStoryConfig, setTrackingStoryConfig] = useState(null);
  const [trackingStoryId, setTrackingStoryId] = useState('');
  
  // PathNav state
  const [isPathNavStory, setIsPathNavStory] = useState(false);
  const [pathNavConfig, setPathNavConfig] = useState(null);
  const [showStyleConfigurator, setShowStyleConfigurator] = useState(false);
  const [exportLevel, setExportLevel] = useState('html'); // html, wordpress, react
  const [showWpBlockInfo, setShowWpBlockInfo] = useState(false);
  const [showWpPackInfo, setShowWpPackInfo] = useState(false);
  const [showWpJsInfo, setShowWpJsInfo] = useState(false);
  
  const iframeRef = useRef(null);
  const textareaRef = useRef(null);

  // Load sample story from URL parameter ?story=<id>
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storyId = urlParams.get('story');
    
    if (storyId) {
      const story = getStoryById(storyId);
      if (story) {
        setTweeCode(story.tweeCode);
        setSelectedStoryId(storyId);
        
        toast({
          title: `Story Loaded: ${story.title}`,
          description: 'The story has been loaded into the editor',
        });
        
        // Auto-expand Step 3 to show the loaded code
        setTimeout(() => {
          setStep3Expanded(true);
          setTimeout(() => {
            document.getElementById('step-3')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }, 500);
      }
    }
  }, []);

  // NOTE: convertGraphToRuntimeStory and convertRuntimeToTweeFormat 
  // are now imported from '../utils/compiler/runtimeConverter'

  // Handler for loading selected story
  const handleLoadStory = () => {
    if (!selectedStoryId) return;

    // If the user has unsaved work, show an AlertDialog (window.confirm is
    // blocked inside sandboxed iframes such as the App Preview).
    if (tweeCode && tweeCode !== initialTwee) {
      setShowLoadStoryConfirmDialog(true);
      return;
    }
    performLoadSelectedStory();
  };

  const performLoadSelectedStory = () => {
    if (!selectedStoryId) return;
    const story = getStoryById(selectedStoryId);
    if (story) {
      // Check if PathNav story
      if (story.isPathNav && story.runtimeStory && story.pathNavConfig) {
        setIsPathNavStory(true);
        setPathNavConfig(story.pathNavConfig);
        setTrackingStoryConfig(story.runtimeStory);
        setTrackingStoryId(story.id);
        setIsTrackingStory(false); // PathNav is separate from tracking stories
        
        // Generate Twee-like representation for display
        const tweeRepresentation = convertRuntimeToTweeFormat(story.runtimeStory);
        setTweeCode(tweeRepresentation);
        
        setShowPreview(false);
        setCompiledHtml('');
        
        toast({
          title: `PathNav Story Loading...`,
          description: 'Auto-compiling and preparing test view...',
        });
        
        setStep3Expanded(true);
        
        // AUTO-COMPILE & AUTO-TEST for PathNav Pro stories
        setTimeout(async () => {
          // Scroll to compile button
          const compileBtn = document.getElementById('compile-button');
          if (compileBtn) {
            compileBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          // Auto-compile the PathNav story
          try {
            await compilePathNavStory(story.runtimeStory, story.id, story.pathNavConfig, exportLevel);
            
            // After compilation, wait a bit then auto-click the test button
            setTimeout(() => {
              setStep4Expanded(true);
              setTimeout(() => {
                const testBtn = document.querySelector('[data-pathnav-test-btn]');
                if (testBtn) {
                  testBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  // Auto-click the test button
                  setTimeout(() => {
                    testBtn.click();
                  }, 500);
                }
              }, 300);
            }, 500);
          } catch (err) {
            console.error('[PathNav Auto-Compile Error]', err);
            toast({
              title: 'Auto-compile failed',
              description: 'Please click Compile Story manually: ' + err.message,
              variant: 'destructive',
            });
          }
        }, 300);
      }
      // Check if this is a tracking story
      else if (story.isTracking && story.runtimeStory) {
        setIsTrackingStory(true);
        setIsPathNavStory(false);
        setTrackingStoryConfig(story.runtimeStory);
        setTrackingStoryId(story.id);
        
        // Generate Twee-like representation for display
        const tweeRepresentation = convertRuntimeToTweeFormat(story.runtimeStory);
        setTweeCode(tweeRepresentation);
        
        setShowPreview(false);
        setCompiledHtml('');
        toast({
          title: `Tracking Story Loaded: ${story.title}`,
          description: 'Review the code, then click "Compile Story" to generate tracking version',
        });
        
        // Expand Step 3 to show the code
        setStep3Expanded(true);
        setTimeout(() => {
          // Scroll to compile button instead of focusing textarea
          const compileBtn = document.getElementById('compile-button');
          if (compileBtn) {
            compileBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        // Regular twee story
        setIsTrackingStory(false);
        setIsPathNavStory(false);
        setTrackingStoryConfig(null);
        setPathNavConfig(null);
        setTweeCode(story.tweeCode);
        toast({
          title: `Story Loaded: ${story.title}`,
          description: 'Ready to compile or edit',
        });
        
        // Expand Step 3 to show the loaded code
        setStep3Expanded(true);
        setTimeout(() => {
          // Scroll to compile button instead of focusing textarea
          const compileBtn = document.getElementById('compile-button');
          if (compileBtn) {
            compileBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  };

  // Handler for saving current twee code
  const handleSaveTwee = () => {
    if (!tweeCode) {
      toast({
        title: 'No content to save',
        description: 'Please write some Twee code first',
        variant: 'destructive',
      });
      return;
    }
    
    // Extract story title from twee code
    const titleMatch = tweeCode.match(/:: StoryTitle\s*\n\s*(.+)/);
    const storyTitle = titleMatch ? titleMatch[1].trim() : 'my-story';
    const filename = storyTitle.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase() + '.twee';
    
    const blob = new Blob([tweeCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Twee Saved!',
      description: `Downloaded as ${filename}`,
    });
  };
  
  // Mind Map Config Block - stored as a special comment in twee code
  const MINDMAP_CONFIG_START = '/* TWEEPORTER_MINDMAP_CONFIG';
  const MINDMAP_CONFIG_END = 'END_MINDMAP_CONFIG */';
  
  // Parse mind map config from twee code
  const parseMindMapConfig = (code) => {
    const configRegex = /\/\* TWEEPORTER_MINDMAP_CONFIG\s*([\s\S]*?)\s*END_MINDMAP_CONFIG \*\//;
    const match = code.match(configRegex);
    if (match) {
      try {
        const configLines = match[1].split('\n').filter(line => line.trim());
        const config = {};
        configLines.forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) {
            config[key] = value;
          }
        });
        return config;
      } catch (e) {
        console.warn('Failed to parse mind map config:', e);
      }
    }
    return null;
  };
  
  // Update mind map config in twee code
  const updateMindMapConfigInCode = (code, config) => {
    const configBlock = `${MINDMAP_CONFIG_START}
direction: ${config.direction}
alignment: ${config.alignment}
showDescription: ${config.showDescription}
charLimit: ${config.charLimit}
${MINDMAP_CONFIG_END}`;
    
    const configRegex = /\/\* TWEEPORTER_MINDMAP_CONFIG[\s\S]*?END_MINDMAP_CONFIG \*\/\n?/;
    if (code.match(configRegex)) {
      // Update existing config
      return code.replace(configRegex, configBlock + '\n');
    } else {
      // Insert at the beginning (before any passages)
      return configBlock + '\n\n' + code;
    }
  };
  
  // Effect to load mind map config when twee code changes (e.g., when loading a story)
  useEffect(() => {
    const config = parseMindMapConfig(tweeCode);
    if (config) {
      if (config.direction) setMindMapDirection(config.direction);
      if (config.alignment) setMindMapAlignment(config.alignment);
      if (config.showDescription !== undefined) setMindMapShowDesc(config.showDescription === 'true');
      if (config.charLimit) setMindMapCharLimit(parseInt(config.charLimit) || 80);
    }
  }, [tweeCode]);
  
  // Effect to update twee code when mind map config changes (only after user explicitly changes config)
  useEffect(() => {
    // Only update if user has explicitly changed config AND we have actual twee content
    if (mindMapConfigUserChanged && tweeCode && tweeCode.includes('::')) {
      const config = {
        direction: mindMapDirection,
        alignment: mindMapAlignment,
        showDescription: mindMapShowDesc,
        charLimit: mindMapCharLimit
      };
      const updatedCode = updateMindMapConfigInCode(tweeCode, config);
      if (updatedCode !== tweeCode) {
        setTweeCode(updatedCode);
      }
    }
  }, [mindMapDirection, mindMapAlignment, mindMapShowDesc, mindMapCharLimit, mindMapConfigUserChanged]);
  
  // Apply preset color to focused field
  const applyPresetColor = (color) => {
    if (focusedColorField === 'bold') setBoldColor(color);
    else if (focusedColorField === 'italic') setItalicColor(color);
    else if (focusedColorField === 'heading') setHeadingColor(color);
    else if (focusedColorField === 'link') setLinkColor(color);
    else if (focusedColorField === 'textBg') setTextBgColor(color);
    else if (focusedColorField === 'frameBg') setFrameBgColor(color);
    // If no field is focused, apply to bold as default
    else setBoldColor(color);
  };

  // Helper function to insert text at cursor position
  const insertAtCursor = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = tweeCode.substring(start, end);
    const newText = tweeCode.substring(0, start) + before + selectedText + after + tweeCode.substring(end);
    
    setTweeCode(newText);
    
    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const formatBold = () => insertAtCursor('**', '**');
  const formatItalic = () => insertAtCursor('*', '*');
  const formatHeading = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const lineStart = tweeCode.lastIndexOf('\n', start - 1) + 1;
    const before = tweeCode.substring(0, lineStart);
    const after = tweeCode.substring(lineStart);
    setTweeCode(before + '## ' + after);
  };
  
  const insertImage = () => {
    setShowImageDialog(true);
  };

  const handleImageInsert = () => {
    if (!imageUrl.trim()) return;
    
    const syntax = `[img[${imageUrl}|${imagePosition}]]`;
    insertAtCursor('\n' + syntax + '\n');
    
    setShowImageDialog(false);
    setImageUrl('');
    setImagePosition('center');
  };

  const insertLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = tweeCode.substring(start, end);

    setLinkSelStart(start);
    setLinkSelEnd(end);

    // If the highlighted text itself looks like a URL, treat it as the target,
    // not the label. Otherwise the highlighted text becomes the display label.
    const looksLikeUrl = /^(https?:\/\/|mailto:|tel:|\/\/)/i.test(selectedText.trim());
    if (looksLikeUrl) {
      setLinkLabel('');
      setLinkTarget(selectedText.trim());
    } else {
      setLinkLabel(selectedText);
      setLinkTarget('');
    }

    setShowLinkDialog(true);
  };

  const confirmInsertLink = () => {
    const target = linkTarget.trim();
    if (!target) return;
    const label = linkLabel.trim() || target;

    // Build [[label->target]]; collapse to [[target]] when label === target.
    const linkStr = label === target
      ? `[[${target}]]`
      : `[[${label}->${target}]]`;

    const newCode =
      tweeCode.substring(0, linkSelStart) +
      linkStr +
      tweeCode.substring(linkSelEnd);

    setTweeCode(newCode);
    setShowLinkDialog(false);

    // Restore textarea focus and put the cursor right after the inserted link.
    setTimeout(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      const cursorPos = linkSelStart + linkStr.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const insertLineBreak = () => insertAtCursor('<br>\n');

  // Table creation functions
  const openTableDialog = () => {
    // Reset to default state
    setTableRows(3);
    setTableCols(3);
    setTableHeaders(['Header 1', 'Header 2', 'Header 3']);
    setTableData([['', ''], ['', '']]);
    setShowTableDialog(true);
  };

  const updateTableSize = (newRows, newCols) => {
    // Update headers array
    const newHeaders = [...tableHeaders];
    while (newHeaders.length < newCols) {
      newHeaders.push(`Header ${newHeaders.length + 1}`);
    }
    if (newHeaders.length > newCols) {
      newHeaders.length = newCols;
    }
    setTableHeaders(newHeaders);

    // Update data array (rows - 1 because first row is headers)
    const dataRows = newRows - 1;
    const newData = [...tableData];
    
    // Adjust number of rows
    while (newData.length < dataRows) {
      newData.push(Array(newCols).fill(''));
    }
    if (newData.length > dataRows) {
      newData.length = dataRows;
    }
    
    // Adjust columns in each row
    for (let i = 0; i < newData.length; i++) {
      while (newData[i].length < newCols) {
        newData[i].push('');
      }
      if (newData[i].length > newCols) {
        newData[i].length = newCols;
      }
    }
    setTableData(newData);
    
    setTableRows(newRows);
    setTableCols(newCols);
  };

  const handleTableInsert = () => {
    // Generate HTML table with proper formatting
    let tableHtml = '\n<table class="story-table">\n';
    
    // Add header row
    tableHtml += '<thead><tr>\n';
    for (const header of tableHeaders) {
      tableHtml += `<th>${header || 'Header'}</th>\n`;
    }
    tableHtml += '</tr></thead>\n';
    
    // Add body rows
    tableHtml += '<tbody>\n';
    for (const row of tableData) {
      tableHtml += '<tr>\n';
      for (const cell of row) {
        tableHtml += `<td>${cell || ''}</td>\n`;
      }
      tableHtml += '</tr>\n';
    }
    tableHtml += '</tbody>\n';
    tableHtml += '</table>\n';
    
    insertAtCursor(tableHtml, '');
    setShowTableDialog(false);
    
    toast({
      title: "Table Inserted",
      description: "HTML table has been added to your story. It will render with proper spacing.",
    });
  };

  // Backend API URL - using centralized config
  const API_URL = API_BASE_URL;

  /**
   * Run backend validation on the current Twee code.
   * Returns the report (or null on network failure). When the report has
   * any errors OR warnings, opens the CompileErrorDialog and returns null
   * (caller should NOT proceed with compile in that case).
   */
  const validateBeforeCompile = async () => {
    setIsValidating(true);
    try {
      const filteredTweeCode = filterTweeByNodeLevel(tweeCode, visualEditorConfig);
      const resp = await fetch(`${API_URL}/api/compile/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twee_code: filteredTweeCode }),
      });
      if (!resp.ok) {
        // Network/server problem — let compile proceed; surface error there.
        return { proceed: true, report: null };
      }
      const report = await resp.json();
      const hasIssues = (report.issues || []).length > 0;
      if (hasIssues) {
        setValidationReport(report);
        setShowValidationDialog(true);
        return { proceed: false, report };
      }
      return { proceed: true, report };
    } catch (err) {
      console.warn('[TweePorter] Validation failed, proceeding to compile anyway:', err);
      return { proceed: true, report: null };
    } finally {
      setIsValidating(false);
    }
  };

  /** Apply selected additive fixes via backend, replace local Twee, then compile. */
  const handleApplyFixesAndCompile = async (fixIds) => {
    setIsValidating(true);
    try {
      const filteredTweeCode = filterTweeByNodeLevel(tweeCode, visualEditorConfig);
      const resp = await fetch(`${API_URL}/api/compile/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          twee_code: filteredTweeCode,
          apply_fix_ids: fixIds,
        }),
      });
      if (!resp.ok) {
        toast({
          title: 'Auto-fix failed',
          description: 'Could not apply fixes. Please fix the issues manually and try again.',
          variant: 'destructive',
        });
        return;
      }
      const data = await resp.json();
      if (data.fixed_twee_code) {
        setTweeCode(data.fixed_twee_code);
      }
      setShowValidationDialog(false);
      setValidationReport(null);
      // Defer compile to next tick so the new Twee code is committed.
      setTimeout(() => compileTwee(), 0);
    } catch (err) {
      console.error('[TweePorter] Apply fixes error:', err);
      toast({
        title: 'Auto-fix failed',
        description: err.message || 'Unable to apply fixes.',
        variant: 'destructive',
      });
    } finally {
      setIsValidating(false);
    }
  };

  const compileTwee = async (shouldAutoScroll = true) => {
    setIsCompiling(true);
    
    try {
      // Filter Twee code based on visual editor config
      const filteredTweeCode = filterTweeByNodeLevel(tweeCode, visualEditorConfig);
      
      // Call backend compiler API
      const response = await fetch(`${API_URL}/api/compile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twee_code: filteredTweeCode,
          layout_option: layoutOption,
          link_display: linkDisplay,
          intro_mode: introMode,
          navigation_option: navigationOption,
          max_passage_length: maxPassageLength,
          style_colors: {
            boldColor,
            italicColor,
            headingColor,
            linkColor,
            textBgColor,
            frameBgColor
          }
        })
      });
      
      // Check content type to ensure we got JSON back
      const contentType = response.headers.get('content-type');
      console.log('[TweePorter] Compile response:', {
        status: response.status,
        contentType: contentType,
        ok: response.ok
      });
      
      if (!contentType || !contentType.includes('application/json')) {
        // Try to get the response text for debugging
        const responseText = await response.text();
        console.error('[TweePorter] Non-JSON response:', responseText.substring(0, 200));
        throw new Error(`Server returned an invalid response (${response.status}). Content-Type: ${contentType || 'none'}`);
      }
      
      // Handle rate limiting
      if (response.status === 429) {
        const errorData = await response.json();
        toast({
          title: 'Rate Limit Exceeded',
          description: errorData.detail?.message || 'Please wait a moment before compiling again.',
          variant: 'destructive',
        });
        setIsCompiling(false);
        return;
      }
      
      if (!response.ok) {
        let errorMessage = 'Compilation failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use default message
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      // Debug logging
      console.log('[TweePorter] Compile result:', {
        success: result.success,
        title: result.story_title,
        passages: result.passage_count,
        htmlLength: result.html?.length || 0,
        hasDoctype: result.html?.includes('<!DOCTYPE') || false
      });
      
      if (!result.success) {
        toast({
          title: 'Compilation Error',
          description: result.error || 'Failed to compile story',
          variant: 'destructive',
        });
        setIsCompiling(false);
        return;
      }
      
      // Validate the HTML before setting
      if (!result.html || result.html.length < 100) {
        console.error('[TweePorter] Invalid HTML received:', result.html);
        toast({
          title: 'Compilation Error',
          description: 'Received invalid HTML from server',
          variant: 'destructive',
        });
        setIsCompiling(false);
        return;
      }
      
      // Set compiled HTML
      console.log('[TweePorter] Setting compiledHtml, length:', result.html.length);
      setCompiledHtml(result.html);
      setStoryTitle(result.story_title);
      setShowPreview(true);
      setPassageCount(result.passage_count);
      
      // Check if story has advanced features (SugarCube macros/variables)
      const hasAdvanced = result.warnings && result.warnings.length > 0;
      setHasAdvancedFeatures(hasAdvanced);
      setJsEnhancedHtml(''); // Reset JS version - user needs to regenerate
      
      // Generate mind map locally (simpler logic, less IP-sensitive)
      let mindMap = generateMindMapHtml(tweeCode, { boldColor, italicColor, headingColor, linkColor, textBgColor, frameBgColor });
      if (introMode) {
        mindMap = addIntroMode(mindMap, result.story_title);
      }
      setMindMapHtml(mindMap);
      
      setIsCompiling(false);
      
      // Clear localStorage after successful compilation
      localStorage.removeItem('compiler_twee_code');
      localStorage.removeItem('compiler_action');
      
      // Show success toast with warning if applicable
      if (result.warnings && result.warnings.length > 0) {
        toast({
          title: 'Story compiled with warnings',
          description: result.warnings[0],
          variant: 'default',
          duration: 8000,
        });
      } else {
        toast({
          title: 'Story compiled!',
          description: 'Your interactive story is ready.',
        });
      }
      
      // Auto-scroll to preview section after manual compilation
      if (shouldAutoScroll) {
        setTimeout(() => {
          // Expand Step 4
          setStep4Expanded(true);
          // Scroll to Step 4
          setTimeout(() => {
            const step4Element = document.getElementById('step-4');
            if (step4Element) {
              step4Element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            } else if (previewRef.current) {
              previewRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            }
          }, 100);
        }, 100);
      }
    } catch (error) {
      console.error('=== COMPILATION ERROR ===', error);
      setIsCompiling(false);
      toast({
        title: 'Error',
        description: error.message || 'Failed to compile story. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Compile tracking-enabled story
  const compileTrackingStory = async (runtimeStory, storyId) => {
    setIsCompiling(true);
    
    try {
      // Read the story template
      const templateResponse = await fetch('/story-runtime/story-template.html');
      const templateHtml = await templateResponse.text();
      
      // Generate unique story ID for analytics
      const analyticsStoryId = storyId + '_' + Date.now();
      
      // Replace the demo story with the actual story configuration
      const storyConfigJson = JSON.stringify(runtimeStory, null, 2);
      
      // Get the current backend URL for analytics
      const backendUrl = API_BASE_URL || window.location.origin;
      
      // Replace the tracking configuration
      let finalHtml = templateHtml.replace(
        /const demoStory = \{[\s\S]*?\};/,
        `const demoStory = ${storyConfigJson};`
      );
      
      // Enable tracking and set analytics URL
      finalHtml = finalHtml.replace(
        /const TRACKING_CONFIG = \{[\s\S]*?\};/,
        `const TRACKING_CONFIG = {
      enabled: true,
      analyticsUrl: '${backendUrl}',
      storyId: '${analyticsStoryId}',
      debugMode: new URLSearchParams(window.location.search).has('debug')
    };`
      );
      
      setCompiledHtml(finalHtml);
      setStoryTitle(runtimeStory.title || 'Interactive Story');
      setShowPreview(true);
      setPassageCount(runtimeStory.nodes?.length || 0);
      setTrackingStoryId(analyticsStoryId);
      
      setIsCompiling(false);
      
      toast({
        title: 'Tracking Story Compiled!',
        description: 'Your story is ready with analytics tracking enabled.',
      });
      
      // Expand Step 4 and scroll
      setTimeout(() => {
        setStep4Expanded(true);
        setTimeout(() => {
          const step4Element = document.getElementById('step-4');
          if (step4Element) {
            step4Element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }, 100);
      
    } catch (error) {
      console.error('=== TRACKING STORY COMPILATION ERROR ===', error);
      setIsCompiling(false);
      toast({
        title: 'Error',
        description: 'Failed to compile tracking story: ' + error.message,
        variant: 'destructive',
      });
    }
  };

  // Compile PathNav story
  const compilePathNavStory = async (runtimeStory, storyId, pathNavConfig, exportLevel) => {
    console.log('[PathNav Compile] Starting compilation...');
    console.log('[PathNav Compile] Story ID:', storyId);
    console.log('[PathNav Compile] Export Level:', exportLevel);
    
    setIsCompiling(true);
    
    try {
      // Read the PathNav template
      console.log('[PathNav Compile] Fetching template...');
      const templateResponse = await fetch('/pathnav-runtime/standalone-template.html');
      
      if (!templateResponse.ok) {
        throw new Error(`Failed to fetch template: ${templateResponse.status} ${templateResponse.statusText}`);
      }
      
      const templateHtml = await templateResponse.text();
      console.log('[PathNav Compile] Template loaded, length:', templateHtml.length);
      
      // Generate unique story ID
      const uniqueStoryId = storyId + '_' + Date.now();
      
      // Prepare configurations as JSON strings
      console.log('[PathNav Compile] Stringifying story config...');
      console.log('[PathNav Compile] Runtime story:', runtimeStory);
      
      let storyConfigJson, nodeStyleJson, connectionStyleJson;
      try {
        storyConfigJson = JSON.stringify(runtimeStory, null, 2);
        console.log('[PathNav Compile] Story config stringified OK');
      } catch (e) {
        console.error('[PathNav Compile] Failed to stringify story config:', e);
        throw new Error('Failed to stringify story configuration: ' + e.message);
      }
      
      try {
        nodeStyleJson = JSON.stringify(pathNavConfig.nodeStyle, null, 2);
        console.log('[PathNav Compile] Node style stringified OK');
      } catch (e) {
        console.error('[PathNav Compile] Failed to stringify node style:', e);
        throw new Error('Failed to stringify node style: ' + e.message);
      }
      
      try {
        connectionStyleJson = JSON.stringify(pathNavConfig.connectionStyle, null, 2);
        console.log('[PathNav Compile] Connection style stringified OK');
      } catch (e) {
        console.error('[PathNav Compile] Failed to stringify connection style:', e);
        throw new Error('Failed to stringify connection style: ' + e.message);
      }
      
      // Determine tracking config based on export level
      const trackingMode = exportLevel === 'wordpress' ? 'wordpress' : exportLevel === 'react' ? 'react' : 'standalone';
      const trackingEndpoint = trackingMode === 'wordpress' 
        ? '/wp-json/tweeporter/v1/analytics' 
        : trackingMode === 'react' 
        ? '/api/tweeporter-analytics/events' 
        : '';
      
      // Replace configurations in template
      let finalHtml = templateHtml
        .replace(/const STORY_CONFIG = \{[\s\S]*?\};/, `const STORY_CONFIG = ${storyConfigJson};`)
        .replace(/const NODE_STYLE = \{[\s\S]*?\};/, `const NODE_STYLE = ${nodeStyleJson};`)
        .replace(/const CONNECTION_STYLE = \{[\s\S]*?\};/, `const CONNECTION_STYLE = ${connectionStyleJson};`)
        .replace(/mode: 'standalone'/, `mode: '${trackingMode}'`)
        .replace(/storyId: STORY_CONFIG.id/, `storyId: '${uniqueStoryId}'`);
      
      if (trackingEndpoint) {
        finalHtml = finalHtml.replace(/endpoint: '.*?'/, `endpoint: '${trackingEndpoint}'`);
      }
      
      console.log('[PathNav Compile] Final HTML length:', finalHtml.length);
      
      setCompiledHtml(finalHtml);
      setStoryTitle(runtimeStory.title || 'PathNav Story');
      setShowPreview(true);
      setPassageCount(runtimeStory.nodes?.length || 0);
      setTrackingStoryId(uniqueStoryId);
      
      setIsCompiling(false);
      
      console.log('[PathNav Compile] Compilation complete! HTML set.');
      
      const exportLevelName = exportLevel === 'wordpress' ? 'WordPress' : exportLevel === 'react' ? 'React' : 'HTML';
      toast({
        title: `PathNav Story Compiled! (${exportLevelName})`,
        description: `Mind map navigation enabled with ${exportLevelName} export`,
      });
      
      // Expand Step 4 and scroll
      setTimeout(() => {
        setStep4Expanded(true);
        setTimeout(() => {
          const step4Element = document.getElementById('step-4');
          if (step4Element) {
            step4Element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }, 100);
      
    } catch (error) {
      console.error('=== PATHNAV COMPILATION ERROR ===', error);
      setIsCompiling(false);
      toast({
        title: 'Error',
        description: 'Failed to compile PathNav story: ' + error.message,
        variant: 'destructive',
      });
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check for Twee code from Sitemap Importer (highest priority)
    const sitemapTwee = localStorage.getItem('compiler_twee_code');
    const sitemapAction = localStorage.getItem('compiler_action');
    
    if (sitemapTwee && sitemapAction === 'import' && !isExample) {
      // Code already loaded via getInitialTwee, just show toast and cleanup
      setStep3Expanded(true);
      setCompiledHtml(''); // Clear any previous compilation
      setMindMapHtml('');
      setShowPreview(false);
      toast({
        title: 'Sitemap Loaded',
        description: 'Your sitemap has been converted to Twee format. Click "Compile Story" to preview!'
      });
      localStorage.removeItem('compiler_twee_code');
      localStorage.removeItem('compiler_action');
      setTimeout(() => {
        document.getElementById('step-3')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return;
    }
    
    // Check for Twee code from Story Planner (high priority)
    const plannerTwee = localStorage.getItem('planner_export_twee');
    const plannerTimestamp = localStorage.getItem('planner_export_timestamp');
    
    if (plannerTwee && plannerTimestamp && !isExample) {
      // Check if export is recent (within last 30 seconds)
      const exportTime = parseInt(plannerTimestamp, 10);
      const now = Date.now();
      if (now - exportTime < 30000) {
        setTweeCode(plannerTwee);
        toast({
          title: 'Story Loaded from Planner',
          description: 'Your story plan has been converted to Twee format. Click "Compile Story" to preview it!'
        });
        // Clear the planner export data
        localStorage.removeItem('planner_export_twee');
        localStorage.removeItem('planner_export_title');
        localStorage.removeItem('planner_export_timestamp');
        return; // Don't check other sources
      }
    }
    
    // Check for Twee code from Visual Editor
    const savedTwee = localStorage.getItem('visual_editor_twee');
    const savedConfig = localStorage.getItem('visual_editor_config');
    const savedGraph = localStorage.getItem('visual_editor_graph');
    const savedSource = localStorage.getItem('visual_editor_source');
    
    // If source is sitemap, treat as PathNav story
    if (savedSource === 'sitemap') {
      setIsPathNavStory(true);
      localStorage.removeItem('visual_editor_source');
    }
    
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setVisualEditorConfig(config);
        // Apply layout direction to layout option
        if (config.layoutDirection === 'down') {
          setLayoutOption('bottom');
        } else if (config.layoutDirection === 'right') {
          setLayoutOption('side');
        }
        
        // DETECT PathNav story from Visual Editor config
        // If it has horizontal layout or PathNav-specific settings, use PathNav compilation
        const isFromVisualEditor = config.layoutDirection === 'right' || 
                                   config.focusNodeViewEnabled || 
                                   config.dynamicViewSize;
        
        if (isFromVisualEditor && savedGraph) {
          try {
            const graph = JSON.parse(savedGraph);
            
            // Convert graph to runtime story format for PathNav
            const runtimeStory = convertGraphToRuntimeStory(graph, savedTwee);
            
            // Create pathNavConfig from visual editor settings
            const pathNavConfigFromEditor = {
              nodeStyle: {
                width: 160,
                height: 40,
                borderRadius: 12,
                fontSize: 14,
                colors: {
                  start: { background: '#10b981', text: '#ffffff', border: '#10b981' },
                  standard: { background: '#667eea', text: '#ffffff', border: '#5a67d8' },
                  light: { background: '#a5b4fc', text: '#1e293b', border: '#818cf8' },
                  unbounded: { background: 'transparent', text: '#667eea', border: '#667eea' }
                }
              },
              connectionStyle: {
                color: '#94a3b8',
                width: 2.5,
                activeColor: '#667eea'
              },
              layout: {
                direction: config.layoutDirection || 'right',
                alignment: 'center',
                gapX: 80,
                gapY: 20
              },
              showDescription: config.showDescription || false,
              focusNodeViewEnabled: config.focusNodeViewEnabled || false,
              focusLayerDepth: config.focusLayerDepth || 2,
              dynamicViewSize: config.dynamicViewSize || 'standard'
            };
            
            setIsPathNavStory(true);
            setPathNavConfig(pathNavConfigFromEditor);
            setTrackingStoryConfig(runtimeStory);
            
            // Set twee code for display
            if (savedTwee) {
              setTweeCode(savedTwee);
            }
            
            toast({
              title: 'PathNav Story Loaded',
              description: 'Mind map from Visual Editor loaded. Click Compile to generate.',
            });
            
            // Auto-compile as PathNav story
            setTimeout(() => {
              compilePathNavStory(runtimeStory, 'visual-editor-' + Date.now(), pathNavConfigFromEditor, 'standalone');
            }, 200);
            
            localStorage.removeItem('visual_editor_config');
            localStorage.removeItem('visual_editor_graph');
            return; // Don't run regular compilation
            
          } catch (graphErr) {
            console.error('Error converting graph to PathNav:', graphErr);
            // Fall back to regular compilation
          }
        }
        
        localStorage.removeItem('visual_editor_config');
      } catch (e) {
        console.error('Error parsing visual editor config:', e);
      }
    }
    
    // Clean up graph from localStorage if not used
    if (savedGraph) {
      localStorage.removeItem('visual_editor_graph');
    }
    
    if (savedTwee && !isExample) {
      setTweeCode(savedTwee);
      toast({
        title: 'Content loaded',
        description: 'Your story from Visual Editor has been loaded'
      });
      // Auto-compile the loaded content
      setTimeout(() => {
        compileTwee(false);
      }, 100);
    }
  }, []);
  
  // Auto-compile on mount if it's an example with initialLayout
  useEffect(() => {
    if (isExample && initialLayout && tweeCode) {
      compileTwee(false); // Don't auto-scroll on page load
    }
  }, []); // Empty dependency array - only run on mount

  // Helper function to process Twee content into HTML
  const processContent = (text) => {
      let processed = text;
      
      // Process Twee image syntax
      processed = processed.replace(/\[img\[([^\]|]+)(?:\|([^\]]+))?\]\]/gi, (match, url, position) => {
        const pos = position ? position.toLowerCase().trim() : 'center';
        return `<img src="${url}" alt="Story image" class="story-image img-${pos}">`;
      });
      
      // Process Markdown images
      processed = processed.replace(/!\[([^\]]*)\]\(([^)|]+)(?:\|([^)]+))?\)/gi, (match, alt, url, position) => {
        const pos = position ? position.toLowerCase().trim() : 'center';
        return `<img src="${url}" alt="${alt}" class="story-image img-${pos}">`;
      });
      
      // Horizontal rules: convert --- or *** to <hr>
      processed = processed.replace(/^[\-\*]{3,}$/gm, '<hr>');
      
      // Convert bullet lists BEFORE bold/italic to avoid conflicts
      const lines = processed.split('\n');
      let inList = false;
      let result = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const bulletMatch = line.match(/^[\*\-]\s+(.+)$/);
        
        if (bulletMatch && !line.match(/^[\*\-]{2,}/)) {
          if (!inList) {
            result.push('<ul>');
            inList = true;
          }
          result.push(`<li>${bulletMatch[1]}</li>`);
        } else {
          if (inList) {
            result.push('</ul>');
            inList = false;
          }
          result.push(line);
        }
      }
      if (inList) {
        result.push('</ul>');
      }
      processed = result.join('\n');
      
      // SUGARCUBE SYNTAX (PRIMARY) - Bold: **text**
      processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      // SUGARCUBE SYNTAX - Italic: *text* (not in lists or strong tags)
      processed = processed.replace(/(?<!<li>|<strong>)\*([^*\n]+)\*(?!<\/strong>)/g, '<em>$1</em>');
      processed = processed.replace(/(?<!<strong>)_([^_\n]+)_(?!<\/strong>)/g, '<em>$1</em>');
      
      // SUGARCUBE SYNTAX - Strikethrough: ~~text~~
      processed = processed.replace(/~~([^~]+)~~/g, '<del>$1</del>');
      
      // SUGARCUBE SYNTAX - Headings: !Heading (SugarCube native)
      processed = processed.replace(/^!{1,6}\s*(.+)$/gm, (match, text) => {
        const level = match.match(/^!+/)[0].length;
        return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
      });
      
      // MARKDOWN SYNTAX - Headings: # text (also supported)
      processed = processed.replace(/^#{1,6}\s+(.+)$/gm, (match, text) => {
        const level = match.match(/^#+/)[0].length;
        return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
      });
      
      // LEGACY TWINE 1 SYNTAX (BACKWARD COMPATIBILITY) - Bold: ''text''
      processed = processed.replace(/''([^']+)''/g, '<strong>$1</strong>');
      
      // LEGACY TWINE 1 SYNTAX - Italic: //text//
      processed = processed.replace(/\/\/([^/]+)\/\//g, '<em>$1</em>');
      
      // LEGACY TWINE 1 SYNTAX - Underline: __text__ (when not used for italic)
      processed = processed.replace(/__([^_]+)__/g, '<u>$1</u>');
      
      // LEGACY TWINE 1 SYNTAX - Strikethrough: ==text==
      processed = processed.replace(/==([^=]+)==/g, '<del>$1</del>');
      
      // Clean up excessive blank lines
      processed = processed.replace(/\n{4,}/g, '\n\n\n');
      
      // Convert newlines to breaks smartly
      processed = processed.replace(/\n{2,3}/g, '<br><br>');
      processed = processed.replace(/\n/g, '<br>');
      
      return processed;
  };

  // generateStoryHtml, parseTwee, splitLongPassages are now imported from '../utils/storyGenerator'
  // Local wrapper to pass maxPassageLength state
  const generateStoryHtmlLocal = (twee, layoutOption = 'bottom', styleColors = {}, linkDisplay = 'buttons', enableIntroMode = false, navOption = 'full') => {
    return generateStoryHtml(twee, layoutOption, styleColors, linkDisplay, enableIntroMode, navOption, maxPassageLength);
  };

  const addIntroMode = (html, storyTitle) => {
    if (!introMode) return html; // If intro mode disabled, return original HTML
    
    // Extract the body content
    const bodyMatch = html.match(/<body>([\s\S]*?)<\/body>/);
    if (!bodyMatch) return html;
    
    const bodyContent = bodyMatch[1];
    
    // Wrap content with intro mode controls
    const wrappedBody = `<body>
    <!-- Intro/Maximize Mode Wrapper -->
    <div id="intro-container" style="
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: 20px;
    ">
        <div style="text-align: center;">
            <h1 style="
                color: white;
                font-size: 2.5em;
                margin-bottom: 24px;
                text-shadow: 0 2px 10px rgba(0,0,0,0.3);
            ">${storyTitle}</h1>
            <button id="maximize-btn" onclick="toggleView()" style="
                background: white;
                color: #667eea;
                border: none;
                padding: 16px 40px;
                font-size: 1.2em;
                font-weight: 600;
                border-radius: 8px;
                cursor: pointer;
                box-shadow: 0 4px 14px rgba(0,0,0,0.2);
                transition: all 0.3s;
            " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                ▶ Maximize Story
            </button>
        </div>
    </div>
    
    <div id="story-view" style="display: none;">
        <!-- Collapse Button -->
        <div style="
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
        ">
            <button id="collapse-btn" onclick="toggleView()" style="
                background: rgba(255,255,255,0.95);
                color: #667eea;
                border: 2px solid #667eea;
                padding: 10px 20px;
                font-size: 0.9em;
                font-weight: 600;
                border-radius: 6px;
                cursor: pointer;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                transition: all 0.2s;
            " onmouseover="this.style.background='#667eea'; this.style.color='white';" onmouseout="this.style.background='rgba(255,255,255,0.95)'; this.style.color='#667eea';">
                ◀ Collapse
            </button>
        </div>
        
        ${bodyContent}
    </div>
    
    <script>
        function toggleView() {
            const intro = document.getElementById('intro-container');
            const story = document.getElementById('story-view');
            
            if (intro.style.display === 'none') {
                // Show intro
                intro.style.display = 'flex';
                story.style.display = 'none';
            } else {
                // Show story
                intro.style.display = 'none';
                story.style.display = 'block';
            }
        }
    </script>
</body>`;
    
    return html.replace(/<body>[\s\S]*?<\/body>/, wrappedBody);
  };


  const openInNewTab = () => {
    if (!compiledHtml) {
      toast({
        title: 'No compiled story',
        description: 'Please compile your story first.',
        variant: 'destructive',
      });
      return;
    }
    
    // For mobile browsers, download the file instead of opening in blob URL
    const blob = new Blob([compiledHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-story.html';
    a.click();
    URL.revokeObjectURL(url);
    
    setTimeout(() => {
      toast({
        title: 'Story downloaded!',
        description: 'Open the downloaded HTML file to play your story.',
      });
    }, 500);
  };

  const downloadHtml = () => {
    if (!compiledHtml) {
      toast({
        title: 'No compiled story',
        description: 'Please compile your story first.',
        variant: 'destructive',
      });
      return;
    }
    
    const blob = new Blob([compiledHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'story.html';
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Downloaded!',
      description: 'Your story has been saved as story.html',
    });
  };

  // Export Format 1: WordPress Custom HTML Block
  const downloadWordPressBlock = () => {
    console.log('downloadWordPressBlock called, compiledHtml:', compiledHtml ? 'exists' : 'null');
    
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    // Extract CSS and HTML from compiledHtml
    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    console.log('CSS match:', cssMatch ? 'found' : 'not found');
    console.log('Body match:', bodyMatch ? 'found' : 'not found');
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    const html = bodyMatch[1];

    // Properly scope CSS for WordPress embedding
    const scopeCss = (cssText) => {
      const scope = '.tweeporter-story';
      let result = '';
      let i = 0;
      
      while (i < cssText.length) {
        // Skip whitespace
        while (i < cssText.length && /\s/.test(cssText[i])) {
          result += cssText[i];
          i++;
        }
        
        // Handle @-rules (keyframes, media queries)
        if (cssText[i] === '@') {
          const atRuleMatch = cssText.slice(i).match(/^@[\w-]+/);
          if (atRuleMatch) {
            const atRule = atRuleMatch[0];
            result += atRule;
            i += atRule.length;
            
            // For @keyframes, copy everything until matching closing brace
            if (atRule === '@keyframes') {
              // Get the keyframe name
              const nameMatch = cssText.slice(i).match(/^\s*([\w-]+)\s*\{/);
              if (nameMatch) {
                result += nameMatch[0];
                i += nameMatch[0].length;
                let braceCount = 1;
                while (i < cssText.length && braceCount > 0) {
                  if (cssText[i] === '{') braceCount++;
                  if (cssText[i] === '}') braceCount--;
                  result += cssText[i];
                  i++;
                }
              }
              continue;
            }
            
            // For @media, get the condition then process inner rules
            if (atRule === '@media') {
              let parenCount = 0;
              while (i < cssText.length && (cssText[i] !== '{' || parenCount > 0)) {
                if (cssText[i] === '(') parenCount++;
                if (cssText[i] === ')') parenCount--;
                result += cssText[i];
                i++;
              }
              result += cssText[i]; // the opening {
              i++;
              continue;
            }
          }
        }
        
        // Handle closing brace
        if (cssText[i] === '}') {
          result += cssText[i];
          i++;
          continue;
        }
        
        // Handle regular selectors - find everything up to {
        let selector = '';
        while (i < cssText.length && cssText[i] !== '{' && cssText[i] !== '}') {
          selector += cssText[i];
          i++;
        }
        
        if (selector.trim() && cssText[i] === '{') {
          // Scope each selector in a comma-separated list
          const scopedSelectors = selector.split(',').map(sel => {
            sel = sel.trim();
            if (!sel) return '';
            // Replace body/html with scope, otherwise prepend scope
            if (/^(body|html)(\s|$|,|{|\[|:|\.)/i.test(sel)) {
              return sel.replace(/^(body|html)/i, scope);
            }
            // Don't double-scope
            if (sel.startsWith(scope)) return sel;
            return `${scope} ${sel}`;
          }).filter(Boolean).join(', ');
          
          result += scopedSelectors;
        }
        
        // Copy the { and rule body
        if (cssText[i] === '{') {
          result += cssText[i];
          i++;
          // Copy until closing }
          let braceCount = 1;
          while (i < cssText.length && braceCount > 0) {
            if (cssText[i] === '{') braceCount++;
            if (cssText[i] === '}') braceCount--;
            if (braceCount > 0) result += cssText[i];
            i++;
          }
          result += '}';
        }
      }
      
      return result;
    };

    const scopedCss = scopeCss(css);

    // Check if we have visual editor config for tree layout
    const useTreeLayout = visualEditorConfig?.focusNodeView || false;
    const layoutDir = visualEditorConfig?.layoutDirection || 'right';

    // Process HTML to make first passage visible and fix script execution
    let processedHtml = html;
    
    // Make the start passage visible by default (add 'active' class)
    // Remove any script tags from the HTML (pure CSS mode doesn't need JS)
    processedHtml = processedHtml.replace(/<script>[\s\S]*?<\/script>/g, '');

    // Pure CSS-only WordPress export - no JS required
    // Uses absolute positioning to prevent scroll jumps
    // Each passage has its own inline footer buttons
    
    // Choose CSS based on layout type
    const treeCss = `
/* WordPress Pure CSS Mode - TREE NAVIGATION LAYOUT */
.tweeporter-story {
  background: transparent !important;
  padding: 0 !important;
}

/* Hide scroll indicator */
.tweeporter-story .story-content-wrapper::after {
  display: none !important;
}

/* Hide the standard header for tree mode */
.tweeporter-story .story-header {
  padding: 16px 20px 8px;
  border-bottom: none;
}

/* Tree layout - compact story container */
.tweeporter-story #story-container {
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

/* Story content wrapper for tree */
.tweeporter-story .story-content-wrapper {
  position: relative !important;
  min-height: 200px;
  overflow: visible !important;
  padding: 16px;
}

/* All passages stacked */
.tweeporter-story .passage {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  display: none !important;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.tweeporter-story .passage#Start {
  position: relative !important;
  display: block !important;
}

.tweeporter-story .passage:target {
  position: relative !important;
  display: block !important;
  z-index: 10;
}

.tweeporter-story:has(.passage:target:not(#Start)) .passage#Start {
  display: none !important;
}

/* Hide shared footer */
.tweeporter-story > .passage-links-footer,
.tweeporter-story #passage-links-container {
  display: none !important;
}

/* Tree-style inline passage buttons */
.tweeporter-story .passage .passage-footer {
  display: flex !important;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0 0;
  margin-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.tweeporter-story .passage .passage-footer a {
  display: block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.tweeporter-story .passage .passage-footer a:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}
`;

    const standardCss = `
/* WordPress Pure CSS Mode - No JavaScript Required */
.tweeporter-story {
  background: transparent !important;
  padding: 0 !important;
}

/* Hide scroll indicator */
.tweeporter-story .story-content-wrapper::after {
  display: none !important;
}

/* Story content wrapper becomes the stacking context */
.tweeporter-story .story-content-wrapper {
  position: relative !important;
  min-height: 300px;
  overflow: visible !important;
}

/* All passages absolutely positioned and stacked */
.tweeporter-story .passage {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  display: none !important;
  background: #fff;
}

/* Show Start passage by default */
.tweeporter-story .passage#Start {
  position: relative !important;
  display: block !important;
}

/* Show targeted passage */
.tweeporter-story .passage:target {
  position: relative !important;
  display: block !important;
  z-index: 10;
}

/* Hide Start when ANY OTHER passage is targeted (uses :has for proper detection) */
.tweeporter-story:has(.passage:target:not(#Start)) .passage#Start {
  display: none !important;
}

/* Hide the shared footer - we use per-passage footers */
.tweeporter-story > .passage-links-footer,
.tweeporter-story #passage-links-container {
  display: none !important;
}

/* Show per-passage footer buttons */
.tweeporter-story .passage .passage-footer {
  display: flex !important;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid #e2e8f0;
}

/* Style the passage links */
.tweeporter-story .passage .passage-footer a {
  display: inline-block;
  background: #667eea;
  color: white !important;
  padding: 10px 16px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.tweeporter-story .passage .passage-footer a:hover {
  filter: brightness(0.85);
  transform: translateY(-2px);
}
`;

    // Select CSS based on layout configuration
    const noJsFallbackCss = useTreeLayout ? treeCss : standardCss;
    // Convert buttons to anchor links
    const convertButtonsToLinks = (htmlContent) => {
      return htmlContent.replace(
        /<button([^>]*?)onclick="navigate\('([^']+)'\)"([^>]*)>([\s\S]*?)<\/button>/gi,
        '<a href="#$2" class="passage-link"$1$3>$4</a>'
      );
    };

    // Move each passage's links-data content into a visible footer inside the passage
    const restructureForPureCss = (htmlContent) => {
      // For each passage, move its passage-links-data content into a visible passage-footer div
      return htmlContent.replace(
        /(<div class="passage[^"]*"[^>]*>)([\s\S]*?)<div class="passage-links-data"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
        (match, passageOpen, content, links) => {
          // Create the passage with inline footer
          return `${passageOpen}${content}<div class="passage-footer">${links}</div></div>`;
        }
      );
    };
    
    // Simplify nav controls to just Reset
    const convertNavControls = (htmlContent) => {
      return htmlContent.replace(
        /<div id="nav-controls"[^>]*>[\s\S]*?<\/div>/gi,
        '<div id="nav-controls" style="display:flex; align-items:center; gap:16px; font-size:0.85em; color:#64748b; margin-bottom:8px;"><a href="#Start" class="nav-link" style="cursor:pointer; color:#667eea; text-decoration:none;">⟲ Reset</a></div>'
      );
    };

    // Remove the shared passage-links-footer since each passage now has its own
    const removeSharedFooter = (htmlContent) => {
      return htmlContent.replace(
        /<div class="passage-links-footer"[^>]*>[\s\S]*?<\/div>\s*(<\/div>\s*)?(<script>|$)/gi,
        '$1$2'
      );
    };

    // Apply transformations
    processedHtml = convertButtonsToLinks(processedHtml);
    processedHtml = restructureForPureCss(processedHtml);
    processedHtml = convertNavControls(processedHtml);
    processedHtml = removeSharedFooter(processedHtml);

    // Build the WordPress block - pure CSS, no JS needed
    const wordpressBlock = `<!-- TweePorter Interactive Story - Pure CSS Mode -->
<!-- No JavaScript required - works on WordPress sites that block scripts -->
<div class="tweeporter-story">
<style>
${scopedCss}
${noJsFallbackCss}
</style>
${processedHtml}
</div>`;

    const blob = new Blob([wordpressBlock], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordpress-story-block.html';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded!', description: 'WordPress Custom HTML Block ready (Pure CSS)' });
  };

  // WordPress Block with JavaScript (for sites that allow JS)
  const downloadWordPressBlockWithJS = () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    // Extract CSS, JavaScript, and HTML from compiledHtml
    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const jsMatch = compiledHtml.match(/<script>([\s\S]*?)<\/script>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    const js = jsMatch ? jsMatch[1] : '';
    const html = bodyMatch[1];

    // Scope CSS for WordPress embedding
    const scopeCss = (cssText) => {
      const scope = '.tweeporter-story';
      let result = '';
      let i = 0;
      
      while (i < cssText.length) {
        while (i < cssText.length && /\s/.test(cssText[i])) {
          result += cssText[i];
          i++;
        }
        
        if (cssText[i] === '@') {
          const atRuleMatch = cssText.slice(i).match(/^@[\w-]+/);
          if (atRuleMatch) {
            const atRule = atRuleMatch[0];
            result += atRule;
            i += atRule.length;
            
            if (atRule === '@keyframes') {
              const nameMatch = cssText.slice(i).match(/^\s*([\w-]+)\s*\{/);
              if (nameMatch) {
                result += nameMatch[0];
                i += nameMatch[0].length;
                let braceCount = 1;
                while (i < cssText.length && braceCount > 0) {
                  if (cssText[i] === '{') braceCount++;
                  if (cssText[i] === '}') braceCount--;
                  result += cssText[i];
                  i++;
                }
              }
              continue;
            }
            
            if (atRule === '@media') {
              let parenCount = 0;
              while (i < cssText.length && (cssText[i] !== '{' || parenCount > 0)) {
                if (cssText[i] === '(') parenCount++;
                if (cssText[i] === ')') parenCount--;
                result += cssText[i];
                i++;
              }
              result += cssText[i];
              i++;
              continue;
            }
          }
        }
        
        if (cssText[i] === '}') {
          result += cssText[i];
          i++;
          continue;
        }
        
        let selector = '';
        while (i < cssText.length && cssText[i] !== '{' && cssText[i] !== '}') {
          selector += cssText[i];
          i++;
        }
        
        if (selector.trim() && cssText[i] === '{') {
          const scopedSelectors = selector.split(',').map(sel => {
            sel = sel.trim();
            if (!sel) return '';
            if (/^(body|html)(\s|$|,|{|\[|:|\.)/i.test(sel)) {
              return sel.replace(/^(body|html)/i, scope);
            }
            if (sel.startsWith(scope)) return sel;
            return `${scope} ${sel}`;
          }).filter(Boolean).join(', ');
          
          result += scopedSelectors;
        }
        
        if (cssText[i] === '{') {
          result += cssText[i];
          i++;
          let braceCount = 1;
          while (i < cssText.length && braceCount > 0) {
            if (cssText[i] === '{') braceCount++;
            if (cssText[i] === '}') braceCount--;
            if (braceCount > 0) result += cssText[i];
            i++;
          }
          result += '}';
        }
      }
      
      return result;
    };

    const scopedCss = scopeCss(css);

    // Wrap JS in an IIFE to avoid global namespace pollution
    const wrappedJs = js ? `
(function() {
  // Scoped to .tweeporter-story container
  const container = document.querySelector('.tweeporter-story');
  if (!container) return;
  
  ${js.replace(/document\.querySelector/g, 'container.querySelector')
      .replace(/document\.querySelectorAll/g, 'container.querySelectorAll')
      .replace(/document\.getElementById/g, 'container.querySelector.bind(container, "#')}
})();
` : '';

    // Build the WordPress block with JS
    const wordpressBlock = `<!-- TweePorter Interactive Story - JavaScript Enabled -->
<!-- Requires site to allow inline JavaScript -->
<div class="tweeporter-story">
<style>
${scopedCss}
</style>
${html}
<script>
${wrappedJs}
</script>
</div>`;

    const blob = new Blob([wordpressBlock], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordpress-story-block-with-js.html';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded!', description: 'WordPress Block with JavaScript ready' });
  };

  // Copy WordPress Block to Clipboard - Uses the clean WP Story Export format
  const copyWordPressBlockToClipboard = async () => {
    if (!tweeCode || !tweeCode.trim()) {
      toast({ title: 'No story', description: 'Please enter or compile a story first.', variant: 'destructive' });
      return;
    }

    try {
      // Generate clean WordPress HTML using the WP Story Export format
      const wordpressBlock = generateWordPressStoryHtml(tweeCode, {
        linkColor: '#667eea',
        textBgColor: '#ffffff',
        frameBgColor: '#f8fafc',
        boldColor: '#fbd38d',
        italicColor: '#b794f4',
        headingColor: '#90cdf4',
        linkDisplay: 'buttons',
        containerWidth: '100%',
        maxWidth: '900px'
      });

      // Copy to clipboard with fallback
      const copyToClipboard = async (text) => {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (e) {
          // Fallback for when Clipboard API is blocked
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.left = '-9999px';
          textarea.style.top = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          
          try {
            const success = document.execCommand('copy');
            document.body.removeChild(textarea);
            return success;
          } catch (err) {
            document.body.removeChild(textarea);
            return false;
          }
        }
      };

      const success = await copyToClipboard(wordpressBlock);
      
      if (success) {
        toast({ title: 'Copied!', description: 'WordPress Block HTML copied to clipboard. Paste into a Custom HTML block.' });
      } else {
        // Last resort: show in a modal/alert for manual copy
        const newWindow = window.open('', '_blank', 'width=800,height=600');
        if (newWindow) {
          newWindow.document.write(`
            <html><head><title>WordPress Block - Copy This</title></head>
            <body style="font-family: sans-serif; padding: 20px;">
              <h2>Copy the code below:</h2>
              <p>Select all (Ctrl+A / Cmd+A) and copy (Ctrl+C / Cmd+C)</p>
              <textarea style="width:100%; height:80vh; font-family: monospace; font-size: 12px;">${wordpressBlock.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
            </body></html>
          `);
          newWindow.document.close();
          toast({ title: 'Opened in new window', description: 'Copy the code from the new window.' });
        } else {
          toast({ title: 'Copy failed', description: 'Please enable popups or use the download button.', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ title: 'Export failed', description: error.message, variant: 'destructive' });
    }
  };

 

  // Export Format 2: WordPress CMS Pack (ZIP)
  const downloadWordPressPack = async () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    let html = bodyMatch[1];
    
    // Remove any script tags (pure CSS mode)
    html = html.replace(/<script>[\s\S]*?<\/script>/g, '');

    // Convert buttons to anchor links for pure CSS navigation
    html = html.replace(
      /<button([^>]*?)onclick="navigate\('([^']+)'\)"([^>]*)>([\s\S]*?)<\/button>/gi,
      '<a href="#$2" class="passage-link"$1$3>$4</a>'
    );

    // Move passage-links-data into visible passage-footer
    html = html.replace(
      /(<div class="passage[^"]*"[^>]*>)([\s\S]*?)<div class="passage-links-data"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
      (match, passageOpen, content, links) => {
        return `${passageOpen}${content}<div class="passage-footer">${links}</div></div>`;
      }
    );

    // Simplify nav controls to just Reset
    html = html.replace(
      /<div id="nav-controls"[^>]*>[\s\S]*?<\/div>/gi,
      '<div id="nav-controls" style="display:flex; align-items:center; gap:16px; font-size:0.85em; color:#64748b; margin-bottom:8px;"><a href="#Start" class="nav-link" style="cursor:pointer; color:#667eea; text-decoration:none;">⟲ Reset</a></div>'
    );

    // Remove shared footer
    html = html.replace(
      /<div class="passage-links-footer"[^>]*>[\s\S]*?<\/div>\s*(<\/div>\s*)?$/gi,
      '$1'
    );

    // Pure CSS overrides for WordPress
    const pureCssOverrides = `
/* WordPress Pure CSS Mode - No JavaScript Required */
/* Hide scroll indicator */
.story-content-wrapper::after {
  display: none !important;
}

/* Story content wrapper becomes the stacking context */
.story-content-wrapper {
  position: relative !important;
  min-height: 300px;
  overflow: visible !important;
}

/* All passages absolutely positioned and stacked */
.passage {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  display: none !important;
  background: #fff;
}

/* Show Start passage by default */
.passage#Start {
  position: relative !important;
  display: block !important;
}

/* Show targeted passage */
.passage:target {
  position: relative !important;
  display: block !important;
  z-index: 10;
}

/* Hide Start when ANY OTHER passage is targeted */
body:has(.passage:target:not(#Start)) .passage#Start {
  display: none !important;
}

/* Hide the shared footer */
.passage-links-footer,
#passage-links-container {
  display: none !important;
}

/* Show per-passage footer buttons */
.passage .passage-footer {
  display: flex !important;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid #e2e8f0;
}

/* Style the passage links */
.passage .passage-footer a {
  display: inline-block;
  background: #667eea;
  color: white !important;
  padding: 10px 16px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.passage .passage-footer a:hover {
  filter: brightness(0.85);
  transform: translateY(-2px);
}
`;

    const fullCss = css + '\n\n' + pureCssOverrides;

    const htmlFile = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${storyTitle}</title>
    <link rel="stylesheet" href="story.css">
</head>
<body>
${html}
</body>
</html>`;

    const readme = `# ${storyTitle} - WordPress Installation Guide

## What's Included
- story.html - The story structure (Pure CSS, no JavaScript)
- story.css - Complete styling with navigation
- README.md - This file

## IMPORTANT: Pure CSS Mode
This export uses a Pure CSS approach for navigation. All JavaScript has been removed
to ensure compatibility with WordPress sites that block inline scripts.

Navigation works via CSS :target selector - clicking links changes the URL hash
and CSS shows/hides the appropriate passage.

## Installation Option 1: Separate Files (Recommended)

### Step 1: Add the CSS
1. Go to WordPress Admin → Appearance → Customize → Additional CSS
2. Open story.css in a text editor
3. Copy ALL contents and paste into the Additional CSS box
4. Click "Publish"

### Step 2: Add the HTML
1. Edit the page where you want the story
2. Add a "Custom HTML" block
3. Open story.html in a text editor
4. Copy everything inside <body>...</body> (not the whole file)
5. Paste into the Custom HTML block
6. Update/Publish the page

## Installation Option 2: Using a Plugin

If you prefer not to modify theme CSS:

1. Install "WPCode" or "Insert Headers and Footers" plugin
2. Add the CSS via the plugin's CSS section
3. Add the HTML via a Custom HTML block as above

## Navigation Features
- Click passage links to navigate between passages
- Click "⟲ Reset" to return to the start
- Use browser Back/Forward buttons for history

## Troubleshooting
- If passages don't switch: Ensure the CSS is loaded (check for styling)
- If both passages show: Your browser may not support :has() - use a modern browser
- If links don't work: Ensure the HTML was copied correctly

## Browser Support
Requires modern browsers with CSS :has() support:
- Chrome 105+
- Safari 15.4+
- Firefox 121+
- Edge 105+

Generated by TweePorter.ca
`;

    const zip = new JSZip();
    zip.file('story.html', htmlFile);
    zip.file('story.css', fullCss);
    zip.file('README.md', readme);

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordpress-story-pack.zip';
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded!', description: 'WordPress Pack (ZIP) ready - Pure CSS mode' });
  };

  // Export Format 3: Universal Raw HTML Snippet
  const downloadUniversalSnippet = () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    const html = bodyMatch[1];

    const scopedCss = css.replace(/(body|html)\s*\{/g, '.story-container {')
                         .replace(/([^}]+)\{/g, '.story-container $1{');

    const snippet = `<!-- Interactive Story - Universal CMS Snippet -->
<div class="story-container">
<style>
${scopedCss}
</style>
${html}
</div>`;

    const blob = new Blob([snippet], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'universal-story-snippet.html';
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded!', description: 'Universal HTML snippet ready' });
  };

  // Export Format 4: Standalone HTML + CSS (two separate files)
  const downloadStandaloneFiles = () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    const html = bodyMatch[1];

    const htmlFile = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${storyTitle}</title>
    <link rel="stylesheet" href="story.css">
</head>
<body>
${html}
</body>
</html>`;

    // Download HTML
    const htmlBlob = new Blob([htmlFile], { type: 'text/html' });
    const htmlUrl = URL.createObjectURL(htmlBlob);
    const htmlA = document.createElement('a');
    htmlA.href = htmlUrl;
    htmlA.download = 'story.html';
    htmlA.click();
    URL.revokeObjectURL(htmlUrl);

    // Download CSS
    setTimeout(() => {
      const cssBlob = new Blob([css], { type: 'text/css' });
      const cssUrl = URL.createObjectURL(cssBlob);
      const cssA = document.createElement('a');
      cssA.href = cssUrl;
      cssA.download = 'story.css';
      cssA.click();
      URL.revokeObjectURL(cssUrl);
    }, 100);

    toast({ title: 'Downloaded!', description: 'story.html and story.css downloaded' });
  };

  // Export to PowerPoint (PPTX)
  const exportToPptx = async () => {
    if (!tweeCode || tweeCode.trim() === '') {
      toast({ title: 'No story to export', description: 'Please write or load a story first.', variant: 'destructive' });
      return;
    }

    try {
      toast({ title: 'Generating PowerPoint...', description: 'Creating your presentation...' });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/pptx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twee_code: tweeCode,
          options: {
            include_back_button: true,
            compact_layout: false,
            theme: 'light'
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate PPTX');
      }

      const blob = await response.blob();
      
      // Try multiple download methods for compatibility
      try {
        // Method 1: Modern download attribute
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'story-slides.pptx';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
        
        toast({ 
          title: 'PowerPoint Downloaded!', 
          description: 'Check your Downloads folder for story-slides.pptx' 
        });
      } catch (downloadError) {
        // Method 2: Fallback - try to save via File System Access API if available
        if (window.showSaveFilePicker) {
          try {
            const handle = await window.showSaveFilePicker({
              suggestedName: 'story-slides.pptx',
              types: [{
                description: 'PowerPoint Presentation',
                accept: { 'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'] }
              }]
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            
            toast({ 
              title: 'PowerPoint Saved!', 
              description: 'Your presentation has been saved.' 
            });
          } catch (saveError) {
            // User cancelled or error - show alternative
            console.warn('Save dialog failed:', saveError);
            showAlternativeDownload(blob);
          }
        } else {
          showAlternativeDownload(blob);
        }
      }
    } catch (error) {
      console.error('PPTX export error:', error);
      toast({ 
        title: 'Export failed', 
        description: 'Could not generate PowerPoint file. Please try again.', 
        variant: 'destructive' 
      });
    }
  };
  
  // Helper function for alternative download
  const showAlternativeDownload = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    toast({ 
      title: 'Download Ready', 
      description: 'PowerPoint opened in new tab. Right-click and "Save As" to download.' 
    });
    
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60000); // Keep URL valid for 1 minute
  };

  // Export Format 5: Minified Single-File HTML
  const downloadMinifiedHtml = () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    // Simple minification: remove comments, extra whitespace
    const minified = compiledHtml
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/\/\*[\s\S]*?\*\//g, '')
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .trim();

    const blob = new Blob([minified], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'story-minified.html';
    a.click();
    URL.revokeObjectURL(url);

    const originalSize = (compiledHtml.length / 1024).toFixed(1);
    const minifiedSize = (minified.length / 1024).toFixed(1);

    toast({ 
      title: 'Downloaded!', 
      description: `Minified: ${originalSize}KB → ${minifiedSize}KB` 
    });
  };

  // Export Format 6: Responsive iFrame Template
  const downloadIframeSnippet = () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const iframeSnippet = `<!-- Responsive iFrame Embed for Interactive Story -->
<div style="position: relative; padding-bottom: 75%; height: 0; overflow: hidden; max-width: 100%;">
    <iframe 
        src="story.html" 
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
        title="${storyTitle}"
        allowfullscreen>
    </iframe>
</div>

<!-- Instructions:
1. Upload story.html to your web server
2. Copy this snippet into your webpage
3. Update the src="story.html" path if needed
4. The iframe will resize responsively (4:3 aspect ratio)
-->`;

    const blob = new Blob([iframeSnippet], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iframe-embed.html';
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded!', description: 'Responsive iFrame embed code ready' });
  };

  // Copy iFrame Snippet to clipboard
  const copyIframeSnippet = async () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const iframeSnippet = `<!-- Responsive iFrame Embed for Interactive Story -->
<div style="position: relative; padding-bottom: 75%; height: 0; overflow: hidden; max-width: 100%;">
    <iframe 
        src="story.html" 
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
        title="${storyTitle}"
        allowfullscreen>
    </iframe>
</div>`;

    try {
      await navigator.clipboard.writeText(iframeSnippet);
      toast({ title: 'Copied!', description: 'iFrame embed code copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  // Copy Universal Snippet to clipboard
  const copyUniversalSnippet = async () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const cssMatch = compiledHtml.match(/<style>([\s\S]*?)<\/style>/);
    const bodyMatch = compiledHtml.match(/<body>([\s\S]*?)<\/body>/);
    
    if (!cssMatch || !bodyMatch) {
      toast({ title: 'Error', description: 'Could not parse story HTML', variant: 'destructive' });
      return;
    }

    const css = cssMatch[1];
    const html = bodyMatch[1];

    const scopedCss = css.replace(/(body|html)\s*\{/g, '.story-container {')
                         .replace(/([^}]+)\{/g, '.story-container $1{');

    const snippet = `<!-- Interactive Story - Universal CMS Snippet -->
<div class="story-container">
<style>
${scopedCss}
</style>
${html}
</div>`;

    try {
      await navigator.clipboard.writeText(snippet);
      toast({ title: 'Copied!', description: 'Universal snippet copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  // Copy Minified HTML to clipboard
  const copyMinifiedHtml = async () => {
    if (!compiledHtml) {
      toast({ title: 'No compiled story', description: 'Please compile first.', variant: 'destructive' });
      return;
    }

    const minified = compiledHtml
      .replace(/\n\s*/g, '')
      .replace(/>\s+</g, '><')
      .replace(/\s{2,}/g, ' ');

    try {
      await navigator.clipboard.writeText(minified);
      toast({ title: 'Copied!', description: 'Minified HTML copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  // Email Preview export removed - email clients don't support JavaScript,
  // making interactive stories impossible to render properly in email.

  // Show reset confirmation dialog
  const handleResetClick = () => {
    setShowResetConfirmDialog(true);
  };
  
  // Perform full reset - clears ALL story buffers across all editors
  const performFullReset = () => {
    // Clear Compiler state
    setTweeCode('');
    setCompiledHtml('');
    setShowPreview(false);
    setStoryTitle('');
    setPassageCount(0);
    setIsPathNavStory(false);
    setPathNavConfig(null);
    setTrackingStoryConfig(null);
    setVisualEditorConfig(null);
    
    // Clear ALL localStorage related to stories
    // Visual Editor
    localStorage.removeItem('visual_editor_twee');
    localStorage.removeItem('visual_editor_graph');
    localStorage.removeItem('visual_editor_config');
    localStorage.removeItem('visual_editor_source');
    localStorage.removeItem('visual_editor_import');
    
    // Visual Story Planner
    localStorage.removeItem('visual_planner_twee');
    localStorage.removeItem('visual_planner_graph');
    localStorage.removeItem('visual_planner_config');
    
    // Compiler
    localStorage.removeItem('compiler_twee_code');
    
    // Session storage
    sessionStorage.removeItem('imported_twee');
    
    setShowResetConfirmDialog(false);
    
    toast({
      title: 'All editors reset',
      description: 'Compiler, Visual Editor, and Story Planner have been cleared.',
    });
  };
  
  // Save current work before reset
  const handleSaveBeforeReset = () => {
    if (tweeCode) {
      // Trigger save
      const titleMatch = tweeCode.match(/:: StoryTitle\s*\n\s*(.+)/);
      const storyTitle = titleMatch ? titleMatch[1].trim() : 'my-story';
      const filename = storyTitle.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase() + '.twee';
      
      const blob = new Blob([tweeCode], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Story saved',
        description: `Downloaded as ${filename}`,
      });
    }
    setShowResetConfirmDialog(false);
  };
  
  // Legacy reset (loads sample story) - kept for backward compatibility
  const resetDemo = () => {
    setTweeCode(initialTwee);
    setCompiledHtml('');
    setShowPreview(false);
    // Clear localStorage to prevent stale data from reloading
    localStorage.removeItem('visual_editor_twee');
    localStorage.removeItem('compiler_twee_code');
    toast({
      title: isExample ? 'Example reset' : 'Compiler reset',
      description: isExample ? 'Example story reloaded.' : 'Sample story loaded.',
    });
  };

  const handleSendToEditor = (content) => {
    // Store the Twee content in sessionStorage
    sessionStorage.setItem('imported_twee', content);
    // Navigate to visual editor
    navigate('/visual-editor');
  };
  
  const handleAIStoryGenerated = (generatedTwee) => {
    setTweeCode(generatedTwee);
    toast({
      title: 'AI Story Loaded',
      description: 'AI-generated story inserted into editor. You may edit freely.'
    });
  };
  
  const handleSendToVisualEditor = () => {
    localStorage.setItem('visual_editor_twee', tweeCode);
    navigate('/visual-editor');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Meta Tags */}
      <SEO 
        title="Twee Compiler - Convert Interactive Stories to HTML | TweePorter"
        description="Free online Twee/SugarCube compiler. Convert your Twine interactive stories into clean, script-free HTML that works on GoDaddy, Wix, Squarespace, and Shopify."
        canonicalUrl="/compiler"
        keywords="Twee compiler, Twine to HTML, SugarCube converter, interactive story compiler, choose your own adventure HTML"
      />
      
      {/* Standard Page Header with StoryNav */}
      <PageHeader borderBottom={true} />

      {/* Action Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-700">{pageTitle}</h2>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/tour/demo-onboarding/step/1')}
              className="hidden md:flex border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Take Tour
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                localStorage.setItem('visual_editor_twee', tweeCode);
                navigate('/visual-editor');
              }} 
              className="hidden sm:flex bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-none hover:from-indigo-600 hover:to-purple-700"
            >
              <Network className="h-4 w-4 mr-2" />
              Visual Editor
            </Button>
            <Button variant="outline" size="sm" onClick={handleResetClick} className="hidden sm:flex">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button variant="outline" size="sm" onClick={downloadHtml} disabled={!compiledHtml} className="hidden sm:flex">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Example Story Banner */}
        {isExample && (
          <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                    {exampleCategory}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{pageTitle}</h2>
                <p className="text-slate-700 leading-relaxed">{exampleDescription}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                <span>Story preloaded. To see it running: expand <strong>Step 4 — Compiled Output</strong>, then click <strong>Play Story in New Tab</strong>.</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Interactive Story Compiler</h1>
            <VersionBadge page="compiler" className="text-slate-400" />
          </div>
          <p className="text-slate-600">Follow the steps below to create your interactive story. Click each step to expand.</p>
        </div>

        <div className="space-y-4">
          
          {/* STEP 1: Create Your Story */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-indigo-300">
            <button
              onClick={() => setStep1Expanded(!step1Expanded)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 hover:from-indigo-100 hover:to-purple-100 transition-colors"
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-indigo-700">Step 1:</span>
                <span className="text-xl font-semibold text-slate-900">Create Your Story</span>
              </div>
              {step1Expanded ? (
                <ChevronUp className="h-6 w-6 text-indigo-600" />
              ) : (
                <ChevronDown className="h-6 w-6 text-indigo-600" />
              )}
            </button>
            
            {step1Expanded && (
              <div className="p-6 space-y-6">
                {/* Option A: Use Your Own AI */}
                <div className="border-2 border-amber-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 border-b border-amber-200">
                    <div className="flex items-center gap-2">
                      <span className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded">Option A</span>
                      <span className="font-semibold text-amber-900">Use Your Own AI (ChatGPT, Claude, etc.)</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-700 mb-3">
                          Copy our <strong>Twee syntax guidelines</strong> and paste them into your preferred AI before asking it to create your story. This ensures the AI generates properly formatted code.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Button
                            size="sm"
                            onClick={() => {
                              const promptInstructions = `IMPORTANT: When generating Twee/Twine story code, follow these syntax rules exactly:

1. LINK SYNTAX - Use ONLY these formats:
   - Pipe syntax: [[Display Text|PassageName]]
   - ASCII arrow: [[Display Text->PassageName]]
   - Simple: [[PassageName]]
   
   ❌ DO NOT use Unicode arrows (→) - use ASCII arrow (->) instead
   ❌ DO NOT use other arrow characters

2. PASSAGE STRUCTURE:
   :: PassageName
   Content goes here
   
   Links go here
   
3. FORMATTING:
   - Bold: **text**
   - Italic: *text* or _text_
   - Strikethrough: ~~text~~
   - Headings: !Heading or # Heading

4. SPECIAL FEATURES SUPPORTED:
   - Images: [img[URL]] or [img[alt text|URL]]
   - Horizontal rule: ---- (four dashes)
   - Line break: <br>
   - Lists: * for bullets, # for numbered

5. STORY TITLE (required):
   :: StoryTitle
   Your Story Title Here

6. START PASSAGE (required):
   :: Start
   Your opening content here
   [[First choice|NextPassage]]

Now generate a Twee story following these rules.`;

                              navigator.clipboard.writeText(promptInstructions).then(() => {
                                toast({
                                  title: 'Guidelines copied!',
                                  description: 'Paste these into your AI chat before requesting a story',
                                });
                              }).catch(() => {
                                toast({
                                  title: 'Copy failed',
                                  description: 'Please select and copy the text manually',
                                  variant: 'destructive',
                                });
                              });
                            }}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            <Copy className="h-3 w-3 mr-2" />
                            Copy Twee Guidelines for AI
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          After your AI generates the code, scroll down to <strong>Step 2</strong>, clear the editor, and paste your code there.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t border-slate-200"></div>
                  <span className="text-sm font-medium text-slate-400">OR</span>
                  <div className="flex-1 border-t border-slate-200"></div>
                </div>

                {/* Option B: Use TweePorter's AI */}
                <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-3 border-b border-purple-200">
                    <div className="flex items-center gap-2">
                      <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">Option B</span>
                      <span className="font-semibold text-purple-900">Use TweePorter&apos;s Built-in AI</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    {/* AI Story Generator Panel */}
                    <AIStoryGeneratorPanel 
                      onStoryGenerated={handleAIStoryGenerated}
                    />
                    
                    {/* AI Usage Limit Tracker */}
                    <div className="mt-4">
                      <AIUsageLimitTracker />
                    </div>
                  </div>
                </div>
                
                {/* Donation Button */}
                <div className="flex justify-center pt-2">
                  <Button 
                    onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=75Y8BAFB8CWDJ', '_blank')}
                    className="bg-pink-600 hover:bg-pink-700 text-white"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    Support TweePorter - Donate
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* STEP 2: Edit Your Code */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-300">
            <button
              onClick={() => setStep2Expanded(!step2Expanded)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 hover:from-green-100 hover:to-emerald-100 transition-colors"
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-green-700">Step 2:</span>
                <span className="text-xl font-semibold text-slate-900">Edit the Formatting</span>
              </div>
              {step2Expanded ? (
                <ChevronUp className="h-6 w-6 text-green-600" />
              ) : (
                <ChevronDown className="h-6 w-6 text-green-600" />
              )}
            </button>
            
            {step2Expanded && (
              <div className="p-6 space-y-4">
                
                {/* Subsection: Story Formatting */}
                <div className="border border-blue-200 rounded-lg">
                  <button
                    onClick={() => setStep2StoryFormatExpanded(!step2StoryFormatExpanded)}
                    className="w-full flex items-center justify-between bg-blue-50 px-4 py-3 hover:bg-blue-100 transition-colors rounded-t-lg"
                    type="button"
                  >
                    <span className="font-semibold text-slate-800">Story Formatting</span>
                    {step2StoryFormatExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                  {step2StoryFormatExpanded && (
                    <div className="px-4 py-3 bg-white space-y-3">
                      
                      {/* Link Display Style */}
                      <div>
                        <label className="text-xs text-indigo-600 font-medium block mb-2">Link Display Style:</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setLinkDisplay('buttons')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              linkDisplay === 'buttons'
                                ? 'bg-indigo-600 text-white shadow-md' 
                                : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                            }`}
                            type="button"
                          >
                            🔘 Buttons (Separate)
                          </button>
                          <button
                            onClick={() => setLinkDisplay('inline')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              linkDisplay === 'inline'
                                ? 'bg-indigo-600 text-white shadow-md' 
                                : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                            }`}
                            type="button"
                          >
                            📝 Inline (In Text)
                          </button>
                        </div>
                        <p className="text-xs text-indigo-600 mt-1.5">
                          {linkDisplay === 'buttons' 
                            ? 'Links shown as separate buttons' 
                            : 'Links embedded directly in the story text'}
                        </p>
                      </div>
                      
                      {/* Button Placement - Only show when Buttons is selected */}
                      {linkDisplay === 'buttons' && (
                        <div>
                          <label className="text-xs text-indigo-600 font-medium block mb-2">Button Placement:</label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setLayoutOption('bottom')}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                layoutOption === 'bottom'
                                  ? 'bg-indigo-600 text-white shadow-md' 
                                  : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                              }`}
                              type="button"
                            >
                              ↓ Bottom
                            </button>
                            <button
                              onClick={() => setLayoutOption('side')}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                layoutOption === 'side'
                                  ? 'bg-indigo-600 text-white shadow-md' 
                                  : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                              }`}
                              type="button"
                            >
                              → Side
                            </button>
                            <button
                              onClick={() => setLayoutOption('leftRight')}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                layoutOption === 'leftRight'
                                  ? 'bg-indigo-600 text-white shadow-md' 
                                  : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                              }`}
                              type="button"
                            >
                              ← Left/Right →
                            </button>
                          </div>
                          <p className="text-xs text-indigo-600 mt-1.5">
                            Choose where navigation buttons appear
                          </p>
                        </div>
                      )}
                      
                      {/* Maximum Passage Length */}
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs text-indigo-600 font-medium">
                            📏 Maximum Passage Length (Mobile-Friendly):
                          </label>
                          <button
                            onClick={() => setShowPassageLengthInfo(true)}
                            className="w-4 h-4 rounded-full border-2 border-indigo-500 text-indigo-500 flex items-center justify-center text-xs font-bold hover:bg-indigo-50 transition-colors"
                            title="Why is this feature useful?"
                            type="button"
                          >
                            i
                          </button>
                        </div>
                        <div className="flex gap-2 items-center">
                          <select
                            value={maxPassageLength}
                            onChange={(e) => setMaxPassageLength(Number(e.target.value))}
                            className="flex-1 px-3 py-2 rounded-lg text-xs border border-indigo-300 bg-white text-indigo-700 font-medium"
                          >
                            <option value="0">Unlimited (No splitting)</option>
                            <option value="500">500 characters (Very Short)</option>
                            <option value="800">800 characters (Short)</option>
                            <option value="1200">1200 characters (Medium)</option>
                            <option value="1800">1800 characters (Long)</option>
                            <option value="2500">2500 characters (Very Long)</option>
                          </select>
                        </div>
                        <p className="text-xs text-indigo-600 mt-1.5">
                          {maxPassageLength === 0 
                            ? 'Passages will not be split (may cause scrolling issues on mobile/CMS)' 
                            : `Long passages will be split at ${maxPassageLength} chars with "Continue..." links`}
                        </p>
                      </div>
                      
                      {/* Intro/Maximize View Mode */}
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="introMode"
                            checked={introMode}
                            onChange={(e) => setIntroMode(e.target.checked)}
                            className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <div className="flex-1">
                            <label htmlFor="introMode" className="text-xs text-indigo-600 font-medium cursor-pointer">
                              📺 Enable Intro/Maximize View Mode
                            </label>
                            <p className="text-xs text-indigo-600 mt-1">
                              When enabled, story starts with just the title and a "Maximize" button. Readers can expand to view the full story or collapse back to the intro view at any time.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Navigation Controls Option */}
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <label className="text-xs text-indigo-600 font-medium block mb-2">🧭 User Navigation Controls:</label>
                        <select
                          value={navigationOption}
                          onChange={(e) => setNavigationOption(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg text-xs border border-indigo-300 bg-white text-indigo-700 font-medium"
                        >
                          <option value="full">Full (Back, Forward, Reset)</option>
                          <option value="minimal">Minimal (Back, Reset only)</option>
                          <option value="none">None (No navigation controls)</option>
                        </select>
                        <p className="text-xs text-indigo-600 mt-1.5">
                          {navigationOption === 'full' 
                            ? 'Users can navigate back, forward, and reset the story' 
                            : navigationOption === 'minimal'
                            ? 'Users can go back and reset (no forward button)'
                            : 'No navigation controls shown (users navigate via story links only)'}
                        </p>
                      </div>
                      
                    </div>
                  )}
                </div>
                
                {/* Subsection: Style Formatting */}
                <div className="border border-purple-200 rounded-lg">
                  <button
                    onClick={() => setStep2StyleFormatExpanded(!step2StyleFormatExpanded)}
                    className="w-full flex items-center justify-between bg-purple-50 px-4 py-3 hover:bg-purple-100 transition-colors rounded-t-lg"
                    type="button"
                  >
                    <span className="font-semibold text-slate-800">Style Formatting</span>
                    {step2StyleFormatExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                  {step2StyleFormatExpanded && (
                    <div className="px-4 py-3 bg-white space-y-3">
                      
                      <p className="text-xs text-purple-700 mb-3">
                        Customize the colors for different text styles in your story
                      </p>
                      
                      {/* Color Preset Palette */}
                      <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                        <label className="text-xs text-purple-700 font-medium block mb-2">Quick Color Presets:</label>
                        <div className="flex flex-wrap gap-2">
                          {['#fbd38d', '#b794f4', '#90cdf4', '#667eea', '#f687b3', '#68d391', '#fc8181', '#f6ad55', '#fbb6ce', '#9f7aea'].map((color) => (
                            <button
                              key={color}
                              onClick={() => applyPresetColor(color)}
                              className="w-8 h-8 rounded border-2 border-white shadow-sm hover:scale-110 transition-transform"
                              style={{ backgroundColor: color }}
                              title={`Apply ${color}`}
                              type="button"
                            />
                          ))}
                        </div>
                        <p className="text-xs text-purple-600 mt-2">
                          Click a preset, then click on any color input below to apply it
                        </p>
                      </div>
                      
                      {/* Bold Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          <Bold className="h-3 w-3 inline mr-1" />
                          Bold Text Color:
                        </label>
                        <div className="flex gap-2 items-center">
                          <input 
                            type="color" 
                            value={boldColor} 
                            onChange={(e) => setBoldColor(e.target.value)}
                            onInput={(e) => setBoldColor(e.target.value)}
                            onFocus={() => setFocusedColorField('bold')}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer"
                          />
                          <input 
                            type="text" 
                            value={boldColor} 
                            onChange={(e) => setBoldColor(e.target.value)}
                            onFocus={() => setFocusedColorField('bold')}
                            placeholder="#000000"
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                          />
                        </div>
                      </div>
                      
                      {/* Italic Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          <Italic className="h-3 w-3 inline mr-1" />
                          Italic Text Color:
                        </label>
                        <div className="flex gap-2 items-center">
                          <input 
                            type="color" 
                            value={italicColor} 
                            onChange={(e) => setItalicColor(e.target.value)}
                            onInput={(e) => setItalicColor(e.target.value)}
                            onFocus={() => setFocusedColorField('italic')}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer"
                          />
                          <input 
                            type="text" 
                            value={italicColor} 
                            onChange={(e) => setItalicColor(e.target.value)}
                            onFocus={() => setFocusedColorField('italic')}
                            placeholder="#000000"
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                          />
                        </div>
                      </div>
                      
                      {/* Heading Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          <Heading2 className="h-3 w-3 inline mr-1" />
                          Heading Color:
                        </label>
                        <div className="flex gap-2 items-center">
                          <input 
                            type="color" 
                            value={headingColor} 
                            onChange={(e) => setHeadingColor(e.target.value)}
                            onInput={(e) => setHeadingColor(e.target.value)}
                            onFocus={() => setFocusedColorField('heading')}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer"
                          />
                          <input 
                            type="text" 
                            value={headingColor} 
                            onChange={(e) => setHeadingColor(e.target.value)}
                            onFocus={() => setFocusedColorField('heading')}
                            placeholder="#000000"
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                          />
                        </div>
                      </div>
                      
                      {/* Link Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          <Link2 className="h-3 w-3 inline mr-1" />
                          Link Color:
                        </label>
                        <div className="flex gap-2 items-center">
                          <input 
                            type="color" 
                            value={linkColor} 
                            onChange={(e) => setLinkColor(e.target.value)}
                            onInput={(e) => setLinkColor(e.target.value)}
                            onFocus={() => setFocusedColorField('link')}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer"
                          />
                          <input 
                            type="text" 
                            value={linkColor} 
                            onChange={(e) => setLinkColor(e.target.value)}
                            onFocus={() => setFocusedColorField('link')}
                            placeholder="#000000"
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                          />
                        </div>
                      </div>
                      
                      {/* Text Background Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          📄 Text Background Color:
                        </label>
                        <div className="flex gap-2 items-center mb-2">
                          <input 
                            type="color" 
                            value={textBgColor === 'transparent' ? '#ffffff' : textBgColor} 
                            onChange={(e) => setTextBgColor(e.target.value)}
                            onInput={(e) => setTextBgColor(e.target.value)}
                            onFocus={() => setFocusedColorField('textBg')}
                            disabled={textBgColor === 'transparent'}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <input 
                            type="text" 
                            value={textBgColor} 
                            onChange={(e) => setTextBgColor(e.target.value)}
                            onFocus={() => setFocusedColorField('textBg')}
                            placeholder="#ffffff"
                            disabled={textBgColor === 'transparent'}
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="textBgTransparent"
                            checked={textBgColor === 'transparent'}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTextBgColor('transparent');
                              } else {
                                setTextBgColor('#ffffff');
                              }
                            }}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <label htmlFor="textBgTransparent" className="text-xs text-purple-600 cursor-pointer">
                            Use Transparent Background
                          </label>
                        </div>
                      </div>
                      
                      {/* Internal Frame Background Color */}
                      <div>
                        <label className="text-xs text-purple-600 font-medium block mb-2">
                          🖼️ Internal Frame Background Color:
                        </label>
                        <div className="flex gap-2 items-center mb-2">
                          <input 
                            type="color" 
                            value={frameBgColor === 'transparent' ? '#f8f9fa' : frameBgColor} 
                            onChange={(e) => setFrameBgColor(e.target.value)}
                            onInput={(e) => setFrameBgColor(e.target.value)}
                            onFocus={() => setFocusedColorField('frameBg')}
                            disabled={frameBgColor === 'transparent'}
                            className="w-10 h-8 rounded border border-slate-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <input 
                            type="text" 
                            value={frameBgColor} 
                            onChange={(e) => setFrameBgColor(e.target.value)}
                            onFocus={() => setFocusedColorField('frameBg')}
                            placeholder="#f8f9fa"
                            disabled={frameBgColor === 'transparent'}
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="frameBgTransparent"
                            checked={frameBgColor === 'transparent'}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFrameBgColor('transparent');
                              } else {
                                setFrameBgColor('#f8f9fa');
                              }
                            }}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <label htmlFor="frameBgTransparent" className="text-xs text-purple-600 cursor-pointer">
                            Use Transparent Background
                          </label>
                        </div>
                      </div>
                      
                    </div>
                  )}
                </div>
                
                {/* Subsection: Mind Map Layout Options (Advanced) */}
                <div className="border border-teal-200 rounded-lg">
                  <button
                    onClick={() => setStep2MindMapExpanded(!step2MindMapExpanded)}
                    className="w-full flex items-center justify-between bg-teal-50 px-4 py-3 hover:bg-teal-100 transition-colors rounded-t-lg"
                    type="button"
                  >
                    <span className="font-semibold text-slate-800">Mind Map Layout Options</span>
                    {step2MindMapExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                  {step2MindMapExpanded && (
                    <div className="px-4 py-3 bg-white space-y-3">
                      <p className="text-xs text-slate-500 mb-3">
                        <strong>For experienced users:</strong> These formatting options are not required for typical story formats. If you want to see the Mind Map layout, create your story, click &quot;Compile Story&quot;, then scroll to the bottom of the Compiled Output and click the &quot;Generate Mind Map&quot; button.
                      </p>
                      
                      {/* Direction */}
                      <div>
                        <label className="text-xs text-teal-600 font-medium block mb-2">Layout Direction:</label>
                        <div className="flex gap-2">
                          {[
                            { value: 'right', label: '→ Right' },
                            { value: 'left', label: '← Left' },
                            { value: 'down', label: '↓ Down' },
                            { value: 'up', label: '↑ Up' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => {
                                setMindMapDirection(opt.value);
                                setMindMapConfigUserChanged(true);
                                // Reset alignment when switching between horizontal/vertical
                                if ((opt.value === 'up' || opt.value === 'down') && (mindMapAlignment === 'above' || mindMapAlignment === 'below')) {
                                  setMindMapAlignment('center');
                                }
                              }}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                mindMapDirection === opt.value
                                  ? 'bg-teal-600 text-white shadow-md' 
                                  : 'bg-white text-teal-600 border border-teal-300 hover:bg-teal-100'
                              }`}
                              type="button"
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Alignment */}
                      <div>
                        <label className="text-xs text-teal-600 font-medium block mb-2">Node Alignment:</label>
                        <div className="flex gap-2">
                          {(mindMapDirection === 'up' || mindMapDirection === 'down') ? (
                            // Vertical layout options
                            [
                              { value: 'center', label: 'Center' },
                              { value: 'below', label: 'Right Only' },
                              { value: 'above', label: 'Left Only' }
                            ].map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => { setMindMapAlignment(opt.value); setMindMapConfigUserChanged(true); }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                  mindMapAlignment === opt.value
                                    ? 'bg-teal-600 text-white shadow-md' 
                                    : 'bg-white text-teal-600 border border-teal-300 hover:bg-teal-100'
                                }`}
                                type="button"
                              >
                                {opt.label}
                              </button>
                            ))
                          ) : (
                            // Horizontal layout options
                            [
                              { value: 'center', label: 'Center' },
                              { value: 'below', label: 'Below Only' },
                              { value: 'above', label: 'Above Only' }
                            ].map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => { setMindMapAlignment(opt.value); setMindMapConfigUserChanged(true); }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                  mindMapAlignment === opt.value
                                    ? 'bg-teal-600 text-white shadow-md' 
                                    : 'bg-white text-teal-600 border border-teal-300 hover:bg-teal-100'
                                }`}
                                type="button"
                              >
                                {opt.label}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                      
                      {/* Show Description */}
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={mindMapShowDesc} 
                            onChange={(e) => { setMindMapShowDesc(e.target.checked); setMindMapConfigUserChanged(true); }}
                            className="w-4 h-4 rounded border-teal-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-xs text-teal-600 font-medium">Show Description in Nodes</span>
                        </label>
                        {mindMapShowDesc && (
                          <div className="flex items-center gap-1">
                            <input 
                              type="number" 
                              value={mindMapCharLimit}
                              onChange={(e) => { setMindMapCharLimit(Math.max(20, Math.min(500, parseInt(e.target.value) || 80))); setMindMapConfigUserChanged(true); }}
                              className="w-16 px-1.5 py-0.5 rounded border border-teal-300 text-center text-teal-700 text-xs"
                              min="20"
                              max="500"
                            />
                            <span className="text-teal-500 text-xs">chars</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-slate-500 mt-2 italic">
                        These settings are saved in your Twee code as a special comment block.
                      </p>
                    </div>
                  )}
                </div>
                
              </div>
            )}
          </div>
          
          {/* PathNav Configuration (Shows for PathNav stories) */}
          {isPathNavStory && pathNavConfig && (
            <div className="bg-indigo-50 rounded-lg border-2 border-indigo-300 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Map className="h-6 w-6 text-indigo-600" />
                <h3 className="text-lg font-semibold text-indigo-900">🗺️ PathNav Configuration (Pro Feature)</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Export Level</label>
                  <select
                    value={exportLevel}
                    onChange={(e) => setExportLevel(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="html">Level 1: Enhanced HTML (Standalone)</option>
                    <option value="wordpress">Level 2: WordPress Plugin</option>
                    <option value="react">Level 3: React Package</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visual Style</label>
                  <Button
                    onClick={() => setShowStyleConfigurator(true)}
                    variant="outline"
                    className="w-full"
                  >
                    🎨 Customize Node & Connections
                  </Button>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                <p className="text-sm text-gray-700 mb-2"><strong>Features Included:</strong></p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>✅ Linear navigation</div>
                  <div>✅ Interactive mind map</div>
                  <div>✅ Expand/collapse</div>
                  <div>✅ Zoom/pan controls</div>
                  <div>✅ Custom styling</div>
                  <div>✅ Analytics tracking</div>
                </div>
              </div>
            </div>
          )}
          
          {/* STEP 3: Coding */}
          <div id="step-3" className="bg-white rounded-lg shadow-sm border-2 border-blue-300">
            <button
              onClick={() => setStep3Expanded(!step3Expanded)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4 hover:from-blue-100 hover:to-cyan-100 transition-colors"
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-blue-700">Step 3:</span>
                <span className="text-xl font-semibold text-slate-900">Coding</span>
              </div>
              {step3Expanded ? (
                <ChevronUp className="h-6 w-6 text-blue-600" />
              ) : (
                <ChevronDown className="h-6 w-6 text-blue-600" />
              )}
            </button>
            
            {step3Expanded && (
              <div className="p-6 space-y-4">
                
                {/* Story Selector Section */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Load Sample Story or Save Your Work
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {/* Left: Load Story */}
                    <div className="space-y-2">
                      <Label htmlFor="story-selector" className="text-sm font-medium text-slate-700">
                        Select a Story
                      </Label>
                      <Select value={selectedStoryId} onValueChange={setSelectedStoryId}>
                        <SelectTrigger id="story-selector" className="w-full">
                          <SelectValue placeholder="Choose a story..." />
                        </SelectTrigger>
                        <SelectContent>
                          {getStoryOptions().map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="text-xs text-slate-500 mr-2">[{option.category}]</span>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        onClick={handleLoadStory}
                        variant="outline"
                        className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Load Story
                      </Button>
                    </div>
                    
                    {/* Right: Save Current Work */}
                    <div className="flex flex-col justify-end">
                      <Button 
                        onClick={handleSaveTwee}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Current Twee
                      </Button>
                      <p className="text-xs text-slate-600 mt-2">
                        💡 Always save your work before loading a new story
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Code Editor */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                  <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                    <span className="font-semibold text-slate-700">Twee Code Editor</span>
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => setShowClearConfirmDialog(true)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="clear-twee-btn"
                      >
                        Clear
                      </Button>
                      <Button 
                        id="compile-button"
                        onClick={async () => {
                          if (isPathNavStory && pathNavConfig && trackingStoryConfig) {
                            compilePathNavStory(trackingStoryConfig, trackingStoryId, pathNavConfig, exportLevel);
                            return;
                          }
                          if (isTrackingStory && trackingStoryConfig) {
                            compileTrackingStory(trackingStoryConfig, trackingStoryId);
                            return;
                          }
                          // Run backend validator first; show dialog if any issues
                          const result = await validateBeforeCompile();
                          if (result.proceed) {
                            compileTwee();
                          }
                        }} 
                        disabled={isCompiling || isValidating}
                        className={`bg-indigo-600 hover:bg-indigo-700 transition-all duration-300 ${
                          isCompiling 
                            ? 'px-6 py-3 text-base min-w-[180px]' 
                            : 'px-5 py-2.5 text-sm min-w-[150px]'
                        }`}
                        style={{ transform: isCompiling ? 'scale(1.1)' : 'scale(1)' }}
                      >
                        {(isCompiling || isValidating) ? (
                          <>
                            <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {isValidating ? 'Validating...' : 'Compiling...'}
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            {compiledHtml ? 'Recompile Story' : 'Compile Story'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {isCompiling && (
                    <div className="bg-amber-100 border-b border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing your story on our secure server...</span>
                    </div>
                  )}
                  
                  {/* Formatting Toolbar */}
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex flex-wrap items-center gap-1">
                    <button
                      onClick={formatBold}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                      title="Bold (**text**)"
                      type="button"
                    >
                      <Bold className="h-4 w-4" />
                    </button>
                    <button
                      onClick={formatItalic}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                      title="Italic (*text*)"
                      type="button"
                    >
                      <Italic className="h-4 w-4" />
                    </button>
                    <button
                      onClick={formatHeading}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                      title="Heading (## text)"
                      type="button"
                    >
                      <Heading2 className="h-4 w-4" />
                    </button>
                    <div className="w-px h-6 bg-slate-300 mx-1"></div>
                    <button
                      onClick={insertImage}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1 text-sm"
                      title="Insert Image"
                      type="button"
                    >
                      <ImageIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Image</span>
                    </button>
                    <button
                      onClick={insertLink}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1 text-sm"
                      title="Insert Link to Passage"
                      type="button"
                    >
                      <Link2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Link</span>
                    </button>
                    <button
                      onClick={insertLineBreak}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                      title="Line Break"
                      type="button"
                    >
                      <CornerDownLeft className="h-4 w-4" />
                    </button>
                    <div className="w-px h-6 bg-slate-300 mx-1"></div>
                    <button
                      onClick={openTableDialog}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1 text-sm"
                      title="Insert Table"
                      type="button"
                    >
                      <Table2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Table</span>
                    </button>
                  </div>
                  
                  <div className="h-[600px]">
                    <textarea
                      ref={textareaRef}
                      value={tweeCode}
                      onChange={(e) => setTweeCode(e.target.value)}
                      className="w-full h-full p-4 font-mono text-sm border-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Paste your Twee code here..."
                      spellCheck="false"
                    />
                  </div>
                </div>

                {/* Syntax Guide */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Sparkles className="h-4 w-4 text-indigo-600" />
                    <h3 className="font-semibold text-indigo-900">SugarCube Twee Format Guide</h3>
                  </div>
                  <div className="text-sm text-indigo-800 space-y-3">
                    {/* Basic Syntax */}
                    <div className="space-y-2">
                      <div className="font-semibold text-indigo-900 text-base">📝 Basic Syntax</div>
                      <div>
                        <strong>Links:</strong> <code className="bg-indigo-100 px-1 rounded">[[Text|Passage]]</code> or <code className="bg-indigo-100 px-1 rounded">[[Text-&gt;Passage]]</code>
                      </div>
                      <div>
                        <strong>Bold:</strong> <code className="bg-indigo-100 px-1 rounded">**text**</code>
                      </div>
                      <div>
                        <strong>Italic:</strong> <code className="bg-indigo-100 px-1 rounded">*text*</code> or <code className="bg-indigo-100 px-1 rounded">_text_</code>
                      </div>
                      <div>
                        <strong>Strikethrough:</strong> <code className="bg-indigo-100 px-1 rounded">~~text~~</code>
                      </div>
                      <div>
                        <strong>Headings:</strong> <code className="bg-indigo-100 px-1 rounded">!Heading</code> (SugarCube) or <code className="bg-indigo-100 px-1 rounded"># Heading</code>
                      </div>
                      <div>
                        <strong>Bullets:</strong> Start lines with <code className="bg-indigo-100 px-1 rounded">* text</code>
                      </div>
                      <div>
                        <strong>Images:</strong> <code className="bg-indigo-100 px-1 rounded">[img[url.jpg|center]]</code>
                        <div className="text-xs ml-4 mt-1">Position options: <code className="bg-indigo-50 px-1">center</code>, <code className="bg-indigo-50 px-1">left</code>, <code className="bg-indigo-50 px-1">right</code></div>
                      </div>
                      <div>
                        <strong>Divider:</strong> <code className="bg-indigo-100 px-1 rounded">---</code> on its own line
                      </div>
                    </div>
                    
                    {/* Special Features */}
                    <div className="pt-3 border-t border-indigo-200 space-y-2">
                      <div className="font-semibold text-indigo-900 text-base">✨ Special Features</div>
                      
                      <div>
                        <strong>Link Effects (Visual Emphasis):</strong>
                        <div className="ml-4 text-xs space-y-1 mt-1">
                          <div><code className="bg-amber-100 px-1 rounded">{`[[{{{pulse}}} Suggested Choice|NextPassage]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Pulsing animation - draws attention to recommended options</div>
                          <div><code className="bg-amber-100 px-1 rounded">{`[[{{{highlight}}} Important Option|NextPassage]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Golden highlighted background - emphasizes key choices</div>
                          <div><code className="bg-amber-100 px-1 rounded">{`[[{{{glow}}} Special Choice|NextPassage]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Glowing shadow effect - indicates special/magical options</div>
                        </div>
                      </div>
                      
                      <div>
                        <strong>Link Positioning (Left/Right Layout):</strong>
                        <div className="ml-4 text-xs space-y-1 mt-1">
                          <div><code className="bg-purple-100 px-1 rounded">{`[[{{{left}}} Go West|WestPath]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Places link in left column (20% width)</div>
                          <div><code className="bg-purple-100 px-1 rounded">{`[[{{{right}}} Go East|EastPath]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Places link in right column (20% width)</div>
                          <div className="text-indigo-600 ml-2 italic">Note: Only works with Left/Right layout option</div>
                        </div>
                      </div>
                      
                      <div>
                        <strong>External Links:</strong>
                        <div className="ml-4 text-xs space-y-1 mt-1">
                          <div><code className="bg-green-100 px-1 rounded">{`[[Visit Website|URL:https://example.com]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Opens external URL in new tab</div>
                          <div className="text-indigo-600 ml-2 italic">Prefix target with "URL:" for external links</div>
                        </div>
                      </div>
                      
                      <div>
                        <strong>Combine Features:</strong>
                        <div className="ml-4 text-xs space-y-1 mt-1">
                          <div><code className="bg-indigo-100 px-1 rounded">{`[[{{{left}}} {{{pulse}}} Best Choice|NextPassage]]`}</code></div>
                          <div className="text-indigo-600 ml-2">→ Pulsing link positioned in left column</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-2 border-t border-indigo-200 text-xs">
                      <em>Legacy Twine 1 syntax (''bold'', //italic//, ==strike==) also supported</em>
                    </div>
                  </div>
                </div>
                
              </div>
            )}
          </div>
          
          {/* STEP 4: Compiled Output */}
          <div id="step-4" className="bg-white rounded-lg shadow-sm border-2 border-purple-300">
            <button
              onClick={() => setStep4Expanded(!step4Expanded)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 hover:from-purple-100 hover:to-pink-100 transition-colors"
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-purple-700">Step 4:</span>
                <span className="text-xl font-semibold text-slate-900">Compiled Output</span>
              </div>
              {step4Expanded ? (
                <ChevronUp className="h-6 w-6 text-purple-600" />
              ) : (
                <ChevronDown className="h-6 w-6 text-purple-600" />
              )}
            </button>
            
            {step4Expanded && (
              <div className="p-6 space-y-4">
                
                {/* Preview Panel */}
                <div className="space-y-4" ref={previewRef}>
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="font-semibold text-slate-700">Live Preview</span>
                        {compiledHtml && (
                          <span className="text-xs text-green-600 font-medium">
                            ✓ Compiled ({Math.round(compiledHtml.length / 1024)} KB)
                          </span>
                        )}
                        {!compiledHtml && <span className="text-xs text-slate-400">Not compiled yet</span>}
                      </div>
                      <div className="flex items-center space-x-2">
                        {compiledHtml && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={downloadHtml}
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => {
                                // Use document.write to avoid blob URL restrictions on links
                                const newWindow = window.open('', '_blank');
                                if (newWindow) {
                                  newWindow.document.write(compiledHtml);
                                  newWindow.document.close();
                                } else {
                                  // Fallback: download the file
                                  const blob = new Blob([compiledHtml], { type: 'text/html' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = 'my-story.html';
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              Open in New Tab
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="min-h-[600px] overflow-y-auto bg-white">
                      {showPreview && compiledHtml ? (
                        <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-8">
                          <div className="text-center max-w-md">
                            <div className="mb-6">
                              <svg className="w-20 h-20 mx-auto text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-indigo-900 mb-2">{storyTitle}</h3>
                            <p className="text-sm text-slate-500 mb-1">Story Compiled Successfully!</p>
                            <p className="text-slate-600 mb-2">
                              Your interactive story is ready with {passageCount} passages.
                            </p>
                            <p className="text-sm text-slate-500 mb-6">
                              Size: {Math.round(compiledHtml.length / 1024)} KB • Pure CSS Navigation
                            </p>
                            <div className="flex flex-col items-center gap-3 w-full">
                              {/* Tracking Story Options */}
                              {isTrackingStory && (
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                                  <div>
                                    <p className="text-sm text-blue-800 font-medium mb-1">📊 Analytics Tracking Enabled</p>
                                    <p className="text-xs text-blue-600">Story ID: {trackingStoryId}</p>
                                  </div>
                                  <div className="bg-white rounded border border-blue-200 p-3">
                                    <p className="text-xs text-slate-700 font-semibold mb-2">ℹ️ How Tracking Works:</p>
                                    <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                                      <li><strong>Story tracks automatically</strong> - No dashboard needed</li>
                                      <li><strong>Dashboard is view-only</strong> - Opens separately to see analytics</li>
                                      <li><strong>All events saved</strong> - View data anytime in dashboard</li>
                                    </ul>
                                  </div>
                                  <p className="text-xs text-blue-700">
                                    💡 <strong>Mobile users:</strong> Bookmark the analytics dashboard URL for easy access.
                                  </p>
                                </div>
                              )}
                              
                              {/* Play Story Options */}
                              <div className="flex flex-col gap-3 w-full">
                                {isTrackingStory ? (
                                  <>
                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                                      <p className="text-xs font-semibold text-slate-700 mb-2">🎯 Choose Your View:</p>
                                      <p className="text-xs text-slate-600">
                                        <strong>Option 1:</strong> Test story only (tracking still works in background)
                                      </p>
                                      <p className="text-xs text-slate-600 mt-1">
                                        <strong>Option 2:</strong> Opens <span className="font-semibold">TWO separate tabs</span> - Story + Dashboard side-by-side
                                      </p>
                                    </div>
                                    
                                    <Button 
                                      size="lg"
                                      onClick={() => {
                                        if (!compiledHtml || compiledHtml.length < 100) {
                                          toast({
                                            title: 'Error',
                                            description: 'No compiled story available.',
                                            variant: 'destructive',
                                          });
                                          return;
                                        }
                                        // Use document.write to avoid blob URL link restrictions
                                        const newWindow = window.open('', '_blank');
                                        if (newWindow) {
                                          newWindow.document.write(compiledHtml);
                                          newWindow.document.close();
                                        }
                                        toast({
                                          title: 'Story Opened!',
                                          description: 'Story opened in new tab (tracking active)',
                                        });
                                      }}
                                      className="bg-indigo-600 hover:bg-indigo-700 px-6 py-4 w-full"
                                    >
                                      <Play className="h-5 w-5 mr-2" />
                                      Option 1: Story Only
                                    </Button>
                                    <Button 
                                      size="lg"
                                      onClick={() => {
                                        if (!trackingStoryId) {
                                          toast({
                                            title: 'Error',
                                            description: 'Story ID not available.',
                                            variant: 'destructive',
                                          });
                                          return;
                                        }
                                        // Open story using document.write to avoid blob URL restrictions
                                        const newWindow = window.open('', '_blank');
                                        if (newWindow) {
                                          newWindow.document.write(compiledHtml);
                                          newWindow.document.close();
                                        }
                                        
                                        // Open analytics dashboard in another tab after a short delay
                                        setTimeout(() => {
                                          const analyticsUrl = `/story-analytics/${trackingStoryId}`;
                                          window.open(analyticsUrl, '_blank');
                                        }, 800);
                                        
                                        toast({
                                          title: 'Opening 2 Tabs...',
                                          description: '1️⃣ Story 2️⃣ Analytics Dashboard',
                                          duration: 5000,
                                        });
                                      }}
                                      className="bg-purple-600 hover:bg-purple-700 px-6 py-4 w-full"
                                    >
                                      <Gamepad2 className="h-5 w-5 mr-2" />
                                      Option 2: Story + Dashboard (2 Tabs)
                                    </Button>
                                  </>
                                ) : (
                                  <Button 
                                    size="lg"
                                    onClick={() => {
                                      console.log('[TweePorter] Play Story clicked, compiledHtml length:', compiledHtml?.length || 0);
                                      if (!compiledHtml || compiledHtml.length < 100) {
                                        toast({
                                          title: 'Error',
                                          description: 'No compiled story available. Please compile first.',
                                          variant: 'destructive',
                                        });
                                        return;
                                      }
                                      // Use document.write to avoid blob URL link restrictions
                                      const newWindow = window.open('', '_blank');
                                      if (newWindow) {
                                        newWindow.document.write(compiledHtml);
                                        newWindow.document.close();
                                      } else {
                                        // Fallback: download the file
                                        const blob = new Blob([compiledHtml], { type: 'text/html' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'my-story.html';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                        toast({
                                          title: 'Downloaded!',
                                          description: 'Open the downloaded file to play your story.',
                                        });
                                      }
                                    }}
                                    className="bg-indigo-600 hover:bg-indigo-700 px-6 py-4 w-auto"
                                  >
                                    <Play className="h-5 w-5 mr-2" />
                                    Play Story in New Tab
                                  </Button>
                                )}
                                <div className="flex items-center gap-2 justify-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={downloadHtml}
                                    className="p-3"
                                    title="Download HTML"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(compiledHtml).then(() => {
                                      toast({
                                        title: 'HTML copied!',
                                        description: 'Story HTML copied to clipboard',
                                      });
                                    }).catch((err) => {
                                      toast({
                                        title: 'Copy failed',
                                        description: err?.message || 'Clipboard access denied. Try the Download button instead.',
                                        variant: 'destructive',
                                      });
                                    });
                                  }}
                                  className="p-3"
                                  title="Copy HTML to Clipboard"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                </div>
                              </div>
                              
                              {/* Play Mind Map Options */}
                              <div className="flex items-center gap-2">
                                <Button 
                                  size="lg"
                                  onClick={() => {
                                    const blob = new Blob([mindMapHtml], { type: 'text/html' });
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 30000);
                                    toast({
                                      title: 'Mind Map opened!',
                                      description: 'Interactive mind map view opened in new tab.',
                                    });
                                  }}
                                  className="bg-emerald-600 hover:bg-emerald-700 px-6 py-5"
                                >
                                  <Map className="h-5 w-5 mr-2" />
                                  Play Mind Map Story in New Tab
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const blob = new Blob([mindMapHtml], { type: 'text/html' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `${storyTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_mindmap.html`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast({
                                      title: 'Mind Map downloaded!',
                                      description: 'Your mind map story has been downloaded.',
                                    });
                                  }}
                                  className="p-3"
                                  title="Download Mind Map HTML"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(mindMapHtml).then(() => {
                                      toast({
                                        title: 'Mind Map HTML copied!',
                                        description: 'Mind map HTML copied to clipboard',
                                      });
                                    }).catch((err) => {
                                      toast({
                                        title: 'Copy failed',
                                        description: err?.message || 'Clipboard access denied. Try the Download button instead.',
                                        variant: 'destructive',
                                      });
                                    });
                                  }}
                                  className="p-3"
                                  title="Copy Mind Map HTML to Clipboard"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* JavaScript Mode Section - Shows when story has variables */}
                              {hasAdvancedFeatures && (
                                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                  <p className="text-sm text-amber-800 mb-3">
                                    <strong>⚡ JavaScript Required:</strong> This story uses variables (like <code className="bg-amber-100 px-1 rounded">$score</code>). 
                                    Use the buttons below to play with working variables and scoring.
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Button 
                                      size="sm"
                                      disabled={isCompilingJs}
                                      onClick={async () => {
                                        setIsCompilingJs(true);
                                        try {
                                          const response = await fetch(`${API_URL}/api/compile`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              twee_code: tweeCode,
                                              layout_option: layoutOption,
                                              link_display: linkDisplay,
                                              intro_mode: introMode,
                                              navigation_option: navigationOption,
                                              enable_javascript: true,
                                              style_colors: { boldColor, italicColor, headingColor, linkColor, textBgColor, frameBgColor }
                                            })
                                          });
                                          if (response.ok) {
                                            const result = await response.json();
                                            if (result.success && result.html) {
                                              setJsEnhancedHtml(result.html);
                                              const blob = new Blob([result.html], { type: 'text/html' });
                                              const url = URL.createObjectURL(blob);
                                              window.open(url, '_blank');
                                              setTimeout(() => URL.revokeObjectURL(url), 30000);
                                              toast({
                                                title: 'JavaScript version opened!',
                                                description: 'Story with working variables opened in new tab.',
                                              });
                                            }
                                          } else {
                                            throw new Error('Failed to compile');
                                          }
                                        } catch (err) {
                                          toast({
                                            title: 'Error',
                                            description: 'Failed to generate JavaScript version.',
                                            variant: 'destructive'
                                          });
                                        } finally {
                                          setIsCompilingJs(false);
                                        }
                                      }}
                                      className="bg-amber-600 hover:bg-amber-700 text-white"
                                    >
                                      {isCompilingJs ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                          Generating...
                                        </>
                                      ) : (
                                        <>
                                          <Play className="h-4 w-4 mr-1" />
                                          Play JS Version
                                        </>
                                      )}
                                    </Button>
                                    
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={!jsEnhancedHtml}
                                      onClick={() => {
                                        if (jsEnhancedHtml) {
                                          const blob = new Blob([jsEnhancedHtml], { type: 'text/html' });
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `${storyTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_js.html`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                          toast({ title: 'Downloaded!', description: 'JavaScript version saved.' });
                                        }
                                      }}
                                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                                      title={jsEnhancedHtml ? "Download JS Version" : "Click 'Play JS Version' first"}
                                    >
                                      <Download className="h-4 w-4 mr-1" />
                                      Download JS
                                    </Button>
                                    
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={!jsEnhancedHtml}
                                      onClick={() => {
                                        if (jsEnhancedHtml) {
                                          navigator.clipboard.writeText(jsEnhancedHtml).then(() => {
                                            toast({ title: 'Copied!', description: 'JavaScript HTML copied to clipboard.' });
                                          }).catch((err) => {
                                            toast({
                                              title: 'Copy failed',
                                              description: err?.message || 'Clipboard access denied. Try the Download button instead.',
                                              variant: 'destructive',
                                            });
                                          });
                                        }
                                      }}
                                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                                      title={jsEnhancedHtml ? "Copy JS HTML" : "Click 'Play JS Version' first"}
                                    >
                                      <Copy className="h-4 w-4 mr-1" />
                                      Copy JS
                                    </Button>
                                  </div>
                                  {jsEnhancedHtml && (
                                    <p className="text-xs text-amber-600 mt-2">
                                      ✓ JavaScript version generated. Download or Copy buttons are now active.
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Ready to Paste Platform Selector */}
                              <div className="mt-8 pt-6 border-t border-slate-200">
                                <h4 className="text-lg font-semibold text-slate-800 mb-2">📋 Ready to Paste?</h4>
                                <p className="text-sm text-slate-600 mb-4">Choose your platform and we'll show you exactly what to do:</p>
                                
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                  <button
                                    onClick={() => {
                                      const platformGuide = `
🎯 FOR WORDPRESS:

1️⃣ In your WordPress editor, click "+" to add a block
2️⃣ Search for "Custom HTML" and select it
3️⃣ Click the "WordPress Block" button below
4️⃣ Copy the entire file content
5️⃣ Paste into the Custom HTML block
6️⃣ Publish!

✅ Works on ALL WordPress plans (including free)
📚 Need help? See our guide: tweeporter.ca/use-case-wordpress.html`;
                                      
                                      toast({
                                        title: '✅ WordPress Instructions',
                                        description: <pre className="text-xs whitespace-pre-wrap mt-2">{platformGuide}</pre>,
                                        duration: 10000
                                      });
                                    }}
                                    className="p-3 sm:p-4 border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-center"
                                  >
                                    <div className="font-semibold text-blue-900 mb-1 text-sm leading-tight">WordPress</div>
                                    <div className="text-xs text-blue-700">All plans</div>
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      const platformGuide = `
🎯 FOR WIX:

1️⃣ In Wix Editor, click "Add" → "Embed"
2️⃣ Choose "Custom Embeds" → "Embed a Site"
3️⃣ Click "iFrame Embed" button below
4️⃣ Upload your HTML file to Wix Media
5️⃣ Copy the file URL and paste into iframe src
6️⃣ Publish!

✅ Works on all Wix plans`;
                                      
                                      toast({
                                        title: '✅ Wix Instructions',
                                        description: <pre className="text-xs whitespace-pre-wrap mt-2">{platformGuide}</pre>,
                                        duration: 10000
                                      });
                                    }}
                                    className="p-3 sm:p-4 border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-center"
                                  >
                                    <div className="font-semibold text-purple-900 mb-1 text-sm leading-tight">Wix</div>
                                    <div className="text-xs text-purple-700">iframe method</div>
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      const platformGuide = `
🎯 FOR SQUARESPACE:

1️⃣ In Squarespace, add a "Code" block
2️⃣ Click "Universal Snippet" button below
3️⃣ Copy the entire HTML content
4️⃣ Paste into the Code block
5️⃣ Save and publish!

✅ Works on Business plan and higher`;
                                      
                                      toast({
                                        title: '✅ Squarespace Instructions',
                                        description: <pre className="text-xs whitespace-pre-wrap mt-2">{platformGuide}</pre>,
                                        duration: 8000
                                      });
                                    }}
                                    className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-center"
                                  >
                                    <div className="font-semibold text-amber-900 mb-1 text-sm leading-tight">Square-space</div>
                                    <div className="text-xs text-amber-700">Code block</div>
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      const platformGuide = `
🎯 FOR OTHER PLATFORMS:

Shopify, Webflow, Notion, Ghost, or any site:

1️⃣ Use "Universal Snippet" for HTML embedding
2️⃣ Or use "iFrame Embed" for maximum compatibility
3️⃣ Follow your platform's custom HTML instructions

✅ Works everywhere that accepts HTML`;
                                      
                                      toast({
                                        title: '✅ Universal Instructions',
                                        description: <pre className="text-xs whitespace-pre-wrap mt-2">{platformGuide}</pre>,
                                        duration: 8000
                                      });
                                    }}
                                    className="p-3 sm:p-4 border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors text-center"
                                  >
                                    <div className="font-semibold text-emerald-900 mb-1 text-sm leading-tight">Other Sites</div>
                                    <div className="text-xs text-emerald-700">Universal</div>
                                  </button>
                                </div>
                                
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
                                  <strong>💡 Tip:</strong> Click your platform above for step-by-step instructions, then use the buttons below to download the right format.
                                </div>
                              </div>
                              
                              {/* Export Formats Section */}
                              <div className="mt-8 pt-6 border-t border-slate-200">
                                <h4 className="text-sm font-semibold text-slate-700 mb-3">All Export Formats:</h4>
                                <div className="space-y-2">
                                  {/* WordPress Block */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-slate-700">WordPress Block</span>
                                      <span className="px-1 py-0.5 bg-purple-600 text-white text-[10px] rounded">PRO</span>
                                      <div className="relative">
                                        <button 
                                          onClick={() => setShowWpBlockInfo(!showWpBlockInfo)}
                                          className="text-slate-400 hover:text-slate-600"
                                        >
                                          <Info className="h-4 w-4" />
                                        </button>
                                        {showWpBlockInfo && (
                                          <div className="absolute left-0 top-6 z-50 w-80 p-4 bg-white rounded-lg shadow-xl border border-slate-200 text-xs">
                                            <button 
                                              onClick={() => setShowWpBlockInfo(false)}
                                              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                                            >×</button>
                                            <h4 className="font-bold text-slate-800 mb-2">WordPress Block (Pure CSS)</h4>
                                            <p className="text-slate-600 mb-2">
                                              <strong>Best for:</strong> Quick embedding into any WordPress page
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>How it works:</strong> Uses Pure CSS navigation (no JavaScript). 
                                              All scripts are removed to ensure compatibility with WordPress sites that block inline JS.
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>How to use:</strong>
                                            </p>
                                            <ol className="list-decimal list-inside text-slate-600 space-y-1 mb-2">
                                              <li>Click Copy or Download</li>
                                              <li>In WordPress, add a "Custom HTML" block</li>
                                              <li>Paste the code</li>
                                              <li>Publish your page</li>
                                            </ol>
                                            <p className="text-amber-600 text-[10px]">
                                              ⚠️ Requires modern browser with CSS :has() support (Chrome 105+, Safari 15.4+, Firefox 121+)
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadWordPressBlock} className="h-8 w-8 p-0" title="Download">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={copyWordPressBlockToClipboard} className="h-8 w-8 p-0" title="Copy to Clipboard">
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* WordPress Block with JavaScript */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-slate-700">WordPress Block + JS</span>
                                      <span className="px-1 py-0.5 bg-green-600 text-white text-[10px] rounded">JS</span>
                                      <div className="relative">
                                        <button 
                                          onClick={() => setShowWpJsInfo(!showWpJsInfo)}
                                          className="text-slate-400 hover:text-slate-600"
                                        >
                                          <Info className="h-4 w-4" />
                                        </button>
                                        {showWpJsInfo && (
                                          <div className="absolute left-0 top-6 z-50 w-80 p-4 bg-white rounded-lg shadow-xl border border-slate-200 text-xs">
                                            <button 
                                              onClick={() => setShowWpJsInfo(false)}
                                              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                                            >×</button>
                                            <h4 className="font-bold text-slate-800 mb-2">WordPress Block (with JavaScript)</h4>
                                            <p className="text-slate-600 mb-2">
                                              <strong>Best for:</strong> Self-hosted WordPress sites that allow JavaScript
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>How it works:</strong> Includes full JavaScript functionality for enhanced interactivity.
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>How to use:</strong>
                                            </p>
                                            <ol className="list-decimal list-inside text-slate-600 space-y-1 mb-2">
                                              <li>Click Download</li>
                                              <li>In WordPress, add a "Custom HTML" block</li>
                                              <li>Paste the code</li>
                                              <li>Publish your page</li>
                                            </ol>
                                            <p className="text-amber-600 text-[10px]">
                                              ⚠️ Only works on sites that allow inline JavaScript (self-hosted WordPress recommended)
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadWordPressBlockWithJS} className="h-8 w-8 p-0" title="Download">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* WordPress Pack ZIP */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-slate-700">WordPress Pack (ZIP)</span>
                                      <span className="px-1 py-0.5 bg-purple-600 text-white text-[10px] rounded">PRO</span>
                                      <div className="relative">
                                        <button 
                                          onClick={() => setShowWpPackInfo(!showWpPackInfo)}
                                          className="text-slate-400 hover:text-slate-600"
                                        >
                                          <Info className="h-4 w-4" />
                                        </button>
                                        {showWpPackInfo && (
                                          <div className="absolute left-0 top-6 z-50 w-80 p-4 bg-white rounded-lg shadow-xl border border-slate-200 text-xs">
                                            <button 
                                              onClick={() => setShowWpPackInfo(false)}
                                              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                                            >×</button>
                                            <h4 className="font-bold text-slate-800 mb-2">WordPress Pack (Pure CSS)</h4>
                                            <p className="text-slate-600 mb-2">
                                              <strong>Best for:</strong> Clean installation with separate CSS file
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>Includes:</strong> story.html, story.css, README.md
                                            </p>
                                            <p className="text-slate-600 mb-2">
                                              <strong>How to use:</strong>
                                            </p>
                                            <ol className="list-decimal list-inside text-slate-600 space-y-1 mb-2">
                                              <li>Download and unzip the file</li>
                                              <li>Go to WordPress → Appearance → Customize → Additional CSS</li>
                                              <li>Paste contents of story.css</li>
                                              <li>Add a "Custom HTML" block to your page</li>
                                              <li>Paste the body content from story.html</li>
                                            </ol>
                                            <p className="text-slate-600 mb-2">
                                              <strong>Why use this?</strong> CSS is cached separately, better for multiple stories on your site.
                                            </p>
                                            <p className="text-amber-600 text-[10px]">
                                              ⚠️ Pure CSS mode - all JavaScript removed for WordPress compatibility
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadWordPressPack} className="h-8 w-8 p-0" title="Download ZIP">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Universal Snippet */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700">Universal Snippet</span>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadUniversalSnippet} className="h-8 w-8 p-0" title="Download">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={copyUniversalSnippet} className="h-8 w-8 p-0" title="Copy to Clipboard">
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* HTML + CSS Files */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700">HTML + CSS Files</span>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadStandaloneFiles} className="h-8 w-8 p-0" title="Download ZIP">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Minified HTML */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700">Minified HTML</span>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadMinifiedHtml} className="h-8 w-8 p-0" title="Download">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={copyMinifiedHtml} className="h-8 w-8 p-0" title="Copy to Clipboard">
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* iFrame Embed */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700">iFrame Embed</span>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={downloadIframeSnippet} className="h-8 w-8 p-0" title="Download">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={copyIframeSnippet} className="h-8 w-8 p-0" title="Copy to Clipboard">
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* PowerPoint */}
                                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700">PowerPoint Slides <span className="ml-1 px-1 py-0.5 bg-purple-600 text-white text-[10px] rounded">PRO</span></span>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={exportToPptx} className="h-8 w-8 p-0" title="Download PPTX">
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-6 pt-6 border-t border-slate-200">
                              <p className="text-xs text-slate-500">
                                The story opens in a new tab where navigation works perfectly on all mobile browsers.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center text-slate-400">
                          <div className="text-center">
                            <p className="text-lg mb-2">No story compiled yet</p>
                            <p className="text-sm">Click "Compile Story" in Step 3 to see your preview here</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* PathNav Pro Features Section */}
                  {isPathNavStory && compiledHtml && (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-lg p-6">
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-indigo-900 mb-2 flex items-center gap-2">
                          <span className="text-2xl">🗺️</span> PathNav Pro Feature
                        </h3>
                        <p className="text-sm text-indigo-700">
                          Export Level: <strong>{exportLevel === 'html' ? 'Enhanced HTML (Standalone)' : exportLevel === 'wordpress' ? 'WordPress Plugin' : 'React Package'}</strong>
                        </p>
                      </div>
                      
                      <div className="bg-white rounded-lg border border-indigo-200 p-4 mb-4">
                        <p className="text-xs text-slate-700 font-semibold mb-3">✨ Included Features:</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Linear navigation
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Mind map view
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Expand/collapse
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Zoom/pan controls
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Custom node styling
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-green-600">✅</span> Analytics tracking
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                        <p className="font-semibold mb-1">💡 How to use:</p>
                        <p>Toggle between <strong>Linear View</strong> and <strong>Mind Map</strong> modes using the buttons at the top of your story.</p>
                      </div>
                      
                      <div className="space-y-3">
                        <Button 
                          size="lg"
                          onClick={() => {
                            console.log('[PathNav] Test button clicked');
                            console.log('[PathNav] compiledHtml length:', compiledHtml?.length);
                            
                            if (!compiledHtml || compiledHtml.length < 100) {
                              console.error('[PathNav] No compiled HTML available');
                              toast({
                                title: 'Error',
                                description: 'No compiled story available. Please compile first.',
                                variant: 'destructive',
                              });
                              return;
                            }
                            
                            try {
                              const blob = new Blob([compiledHtml], { type: 'text/html' });
                              const url = URL.createObjectURL(blob);
                              console.log('[PathNav] Blob URL created:', url);
                              
                              const newWindow = window.open(url, '_blank');
                              
                              // More robust popup detection - check after a small delay
                              setTimeout(() => {
                                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                                  console.warn('[PathNav] Popup blocked, trying alternative');
                                  toast({
                                    title: '⚠️ Popup Blocked',
                                    description: 'Your browser blocked the popup. Click "Download HTML" below to get the file.',
                                    variant: 'destructive',
                                    duration: 10000,
                                  });
                                  
                                  // Auto-download as fallback
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = 'pathnav-story.html';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  
                                  toast({
                                    title: '📥 Downloaded',
                                    description: 'Story downloaded as pathnav-story.html. Open the file in your browser to test.',
                                    duration: 8000,
                                  });
                                } else {
                                  console.log('[PathNav] Window opened successfully');
                                  toast({
                                    title: 'PathNav Story Opened!',
                                    description: 'Test linear and mind map views in the new tab',
                                  });
                                }
                              }, 100);
                              
                              setTimeout(() => URL.revokeObjectURL(url), 30000);
                            } catch (error) {
                              console.error('[PathNav] Error opening story:', error);
                              toast({
                                title: 'Error',
                                description: 'Failed to open story: ' + error.message,
                                variant: 'destructive',
                              });
                            }
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700 px-6 py-4 w-full"
                          data-pathnav-test-btn="true"
                        >
                          <Play className="h-5 w-5 mr-2" />
                          Test PathNav Story (Linear + Mind Map)
                        </Button>
                        
                        <Button 
                          size="lg"
                          onClick={() => {
                            const blob = new Blob([compiledHtml], { type: 'text/html' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            const exportName = exportLevel === 'html' ? 'pathnav-story.html' : 
                                              exportLevel === 'wordpress' ? 'pathnav-wordpress-plugin.html' : 
                                              'pathnav-react-component.html';
                            link.download = exportName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => URL.revokeObjectURL(url), 100);
                            
                            toast({
                              title: 'Download Started!',
                              description: `${exportLevel === 'html' ? 'HTML' : exportLevel === 'wordpress' ? 'WordPress' : 'React'} export downloaded`,
                            });
                          }}
                          variant="outline"
                          className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        >
                          <Download className="h-5 w-5 mr-2" />
                          Download {exportLevel === 'html' ? 'HTML' : exportLevel === 'wordpress' ? 'WordPress Plugin' : 'React Package'}
                        </Button>
                        
                        {exportLevel === 'wordpress' && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                            <p className="font-semibold mb-1">📦 WordPress Installation:</p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Upload to /wp-content/plugins/tweeporter-pathnav/</li>
                              <li>Activate in WordPress admin</li>
                              <li>Use shortcode: [tweeporter_story id="your-story"]</li>
                            </ol>
                          </div>
                        )}
                        
                        {exportLevel === 'react' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                            <p className="font-semibold mb-1">⚛️ React Integration:</p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Copy react-component folder to your project</li>
                              <li>Import TweeporterPathNav component</li>
                              <li>Pass storyConfig and trackingConfig props</li>
                              <li>See README.md for full documentation</li>
                            </ol>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Visual Editor Button */}
                  {compiledHtml && (
                    <div className="text-center bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-6">
                      <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <div>
                          <Button 
                            onClick={handleSendToVisualEditor}
                            size="lg"
                            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-6 text-lg"
                          >
                            <Network className="h-5 w-5 mr-2" />
                            Send to Visual Editor (Beta)
                          </Button>
                          <p className="text-sm text-slate-600 mt-2">
                            Continue editing your story visually with drag-and-drop nodes
                          </p>
                        </div>
                        <div>
                          <Button 
                            onClick={() => setShowLiveSession(true)}
                            size="lg"
                            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg"
                          >
                            <Gamepad2 className="h-5 w-5 mr-2" />
                            Shared Story (Beta)
                          </Button>
                          <p className="text-sm text-slate-600 mt-2">
                            Run live sessions with participants - chat, vote, guided choices
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
              </div>
            )}
          </div>
          
        </div>
      </main>

      {/* Link Insert Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md" data-testid="link-insert-dialog">
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
            <DialogDescription>
              Wrap the selected text in a link, or create a new one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="link-label-input">Display text</Label>
              <Input
                id="link-label-input"
                data-testid="link-label-input"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder="What the reader sees"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); confirmInsertLink(); }
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="link-target-input">Link target</Label>
              <Input
                id="link-target-input"
                data-testid="link-target-input"
                value={linkTarget}
                onChange={(e) => setLinkTarget(e.target.value)}
                placeholder="https://example.com  or  PassageName"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); confirmInsertLink(); }
                }}
              />
              <p className="text-xs text-slate-500">
                Paste a URL (https://…, mailto:, tel:) for an external link, or type a passage name for an internal jump.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
              data-testid="link-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmInsertLink}
              disabled={!linkTarget.trim()}
              data-testid="link-insert-button"
            >
              Insert Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Insert Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
            <DialogDescription>
              Enter the image URL and choose its position in the passage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="imageUrl">Image URL</Label>
              <Input
                id="imageUrl"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              <p className="mt-2 text-xs text-slate-500">
                <strong>Note:</strong> We suggest you use direct image links from services such as Imgur, PostImages, or ImgBB. Links from Google Drive, Dropbox, and OneDrive may not display correctly.
              </p>
            </div>
            <div>
              <Label htmlFor="imagePosition">Position</Label>
              <Select value={imagePosition} onValueChange={setImagePosition}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImageInsert}>
              Insert Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Insert Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="h-5 w-5 text-indigo-600" />
              Create Table
            </DialogTitle>
            <DialogDescription>
              Build an HTML table for your story. This creates properly formatted tables with consistent spacing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Size Controls */}
            <div className="flex items-center gap-6">
              <div>
                <Label htmlFor="tableRows">Rows (including header)</Label>
                <Select 
                  value={tableRows.toString()} 
                  onValueChange={(v) => updateTableSize(parseInt(v), tableCols)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tableCols">Columns</Label>
                <Select 
                  value={tableCols.toString()} 
                  onValueChange={(v) => updateTableSize(tableRows, parseInt(v))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5, 6].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table Preview/Editor */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                {/* Header Row */}
                <thead className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                  <tr>
                    {tableHeaders.map((header, i) => (
                      <th key={i} className="px-3 py-2 border-r border-indigo-400 last:border-r-0">
                        <Input
                          value={header}
                          onChange={(e) => {
                            const newHeaders = [...tableHeaders];
                            newHeaders[i] = e.target.value;
                            setTableHeaders(newHeaders);
                          }}
                          className="bg-white/20 border-0 text-white placeholder:text-white/60 text-center font-semibold h-8"
                          placeholder={`Header ${i + 1}`}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                {/* Body Rows */}
                <tbody>
                  {tableData.map((row, rowIdx) => (
                    <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                      {row.map((cell, colIdx) => (
                        <td key={colIdx} className="px-3 py-2 border-t border-slate-200">
                          <Input
                            value={cell}
                            onChange={(e) => {
                              const newData = [...tableData];
                              newData[rowIdx][colIdx] = e.target.value;
                              setTableData(newData);
                            }}
                            className="border-slate-300 h-8"
                            placeholder="Cell content"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800">
                <strong>💡 Tip:</strong> This creates an HTML table that renders correctly with proper spacing 
                above and below. Unlike markdown tables, HTML tables won&apos;t have line break issues.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTableDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTableInsert} className="bg-indigo-600 hover:bg-indigo-700">
              <Table2 className="h-4 w-4 mr-2" />
              Insert Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passage Length Info Dialog */}
      <Dialog open={showPassageLengthInfo} onOpenChange={setShowPassageLengthInfo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Why Use Maximum Passage Length?</DialogTitle>
            <DialogDescription>
              This feature solves a common problem with interactive stories on mobile devices and CMS platforms
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 text-sm">
            <div>
              <h4 className="font-semibold text-indigo-700 mb-2">🎯 The Problem</h4>
              <p className="text-slate-700 leading-relaxed">
                When interactive stories are displayed on mobile devices or embedded in CMS platforms (WordPress, Squarespace, etc.), 
                long passages create a hidden content problem. Users see a block of text that appears complete, but they don't realize 
                they need to scroll down to see navigation buttons or additional content. This creates a frustrating "dead-end" experience 
                where readers think the story has ended or is broken.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-indigo-700 mb-2">🚫 Why It's Hard to Fix in CMS</h4>
              <p className="text-slate-700 leading-relaxed mb-2">
                Most CMS platforms have limitations that make this difficult to solve at the display level:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Restricted CSS control in page builders (Wix, GoDaddy, basic WordPress blocks)</li>
                <li>Fixed container heights that hide overflow content</li>
                <li>Mobile viewport inconsistencies across different themes and templates</li>
                <li>Limited JavaScript capabilities in sandboxed environments</li>
                <li>No way to add scroll indicators or visual hints in many platforms</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-green-700 mb-2">✅ The Solution: Smart Content Splitting</h4>
              <p className="text-slate-700 leading-relaxed mb-2">
                This feature solves the problem at the source by automatically breaking long passages into digestible chunks:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong>Intelligent Breaking:</strong> Splits at natural paragraph breaks, not mid-sentence</li>
                <li><strong>Automatic Links:</strong> Adds "Continue reading..." navigation between chunks</li>
                <li><strong>Universal Fix:</strong> Works in ALL export formats (WordPress, iframe, email, standalone HTML)</li>
                <li><strong>Mobile-Optimized:</strong> Ensures content fits on small screens without hidden elements</li>
                <li><strong>Reader-Friendly:</strong> Creates natural reading flow with clear progression</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-indigo-700 mb-2">💡 When to Use This Feature</h4>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong>800-1200 characters:</strong> Ideal for mobile-first experiences and restrictive CMS platforms</li>
                <li><strong>1200-1800 characters:</strong> Good balance for mixed desktop/mobile audiences</li>
                <li><strong>2500+ characters:</strong> Best for desktop-focused stories or when you have full CSS control</li>
                <li><strong>Unlimited:</strong> Use only when deploying to platforms where you control the entire layout</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800 font-medium">
                💡 <strong>Pro Tip:</strong> If you're exporting for WordPress, Wix, Squarespace, or email newsletters, 
                start with 800-1200 characters. You can always increase it if your specific platform handles longer content well.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPassageLengthInfo(false)} className="bg-indigo-600 hover:bg-indigo-700">
              Got it, thanks!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirmDialog} onOpenChange={setShowResetConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="h-5 w-5" />
              Reset All Editors?
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              This will clear all unsaved work in:
              <ul className="list-disc list-inside mt-2 space-y-1 text-slate-600">
                <li>Compiler (Twee code & compiled output)</li>
                <li>Visual Editor (mind map & graph)</li>
                <li>Visual Story Planner (story structure)</li>
              </ul>
              <p className="mt-3 font-medium text-slate-700">
                All changes will be lost. This cannot be undone.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowResetConfirmDialog(false)}
              className="sm:mr-auto"
            >
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={handleSaveBeforeReset}
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            >
              <Save className="h-4 w-4 mr-2" />
              Save First
            </Button>
            <Button 
              onClick={performFullReset}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              I Understand, Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live Session Panel */}
      <LiveSessionPanel
        isOpen={showLiveSession}
        onClose={() => setShowLiveSession(false)}
        storyId={selectedStoryId || 'default-story'}
        storyTitle={storyTitle}
        currentNodeId={currentPassage.id}
        currentNodeTitle={currentPassage.title}
        currentNodeContent={currentPassage.content}
        tweeSource={tweeCode}
        allPassages={extractPassagesFromTwee(tweeCode)}
      />
      
      {/* PathNav Style Configurator Modal */}
      {showStyleConfigurator && pathNavConfig && pathNavConfig.nodeStyle && pathNavConfig.connectionStyle && (
        <PathNavStyleConfigurator
          initialNodeStyle={pathNavConfig.nodeStyle}
          initialConnectionStyle={pathNavConfig.connectionStyle}
          onSave={({ nodeStyle, connectionStyle }) => {
            setPathNavConfig(prev => ({
              ...prev,
              nodeStyle,
              connectionStyle
            }));
            toast({
              title: 'Style Saved',
              description: 'Your custom PathNav style has been applied'
            });
          }}
          onClose={() => setShowStyleConfigurator(false)}
        />
      )}

      {/* Compile validation / auto-correct dialog */}
      <CompileErrorDialog
        open={showValidationDialog}
        report={validationReport}
        isProcessing={isValidating || isCompiling}
        onClose={() => {
          setShowValidationDialog(false);
          setValidationReport(null);
        }}
        onApplyFixes={handleApplyFixesAndCompile}
        onCompileAnyway={() => {
          setShowValidationDialog(false);
          setValidationReport(null);
          compileTwee();
        }}
      />

      {/* Clear-content confirmation (replaces window.confirm) */}
      <AlertDialog open={showClearConfirmDialog} onOpenChange={setShowClearConfirmDialog}>
        <AlertDialogContent data-testid="clear-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all content?</AlertDialogTitle>
            <AlertDialogDescription>
              This will erase the current Twee code in the editor and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-confirm-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="clear-confirm-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setTweeCode('');
                textareaRef.current?.focus();
              }}
            >
              Clear content
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load-library-story confirmation (replaces window.confirm) */}
      <AlertDialog
        open={showLoadStoryConfirmDialog}
        onOpenChange={setShowLoadStoryConfirmDialog}
      >
        <AlertDialogContent data-testid="load-story-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your current work?</AlertDialogTitle>
            <AlertDialogDescription>
              Loading this library story will replace whatever is in the Twee
              editor. Make sure you've saved your work first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="load-story-confirm-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="load-story-confirm-confirm-btn"
              onClick={() => {
                setShowLoadStoryConfirmDialog(false);
                performLoadSelectedStory();
              }}
            >
              Yes, replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Compiler;
